import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate, Link, useLocation as useRouterLocation } from 'react-router-dom';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import { useLocation } from '../../contexts/LocationContext';
import { useSearchState, DEFAULT_FILTERS } from '../../contexts/SearchStateContext';
import { useTranslation } from '../../contexts/TranslationContext';
import SearchBar from '../../components/searchBar/searchBar';
import ProviderFilters from '../../components/providerFilters/providerFilters';
import ResultsSummary from '../../components/resultsSummary/resultsSummary';
import ResultsControlBar from '../../components/resultsControlBar/resultsControlBar';
import Pagination from '../../components/pagination/pagination';
import EmailModal from '../../components/emailModal/emailModal';
import PrintEmailModal from '../../components/printEmailModal/printEmailModal';
import IncorrectInfoModal from '../../components/incorrectInfoModal/incorrectInfoModal';
import DistancePanel from '../../components/distancePanel/distancePanel';
import config from '../../config/appConfig';
import { apicFetch } from '../../services/apiTokenService';
import locationService from '../../services/locationService';
import { resolveExpectedStateCode, isLocationInExpectedState } from '../../utils/locationStateValidation';
import ProviderSummary from '../../components/providerSummary/providerSummary';
import ProviderDetails from '../../components/providerDetails/providerDetails';
import ProviderDetailsType2 from '../../components/providerDetailsType2/providerDetailsType2';
import ProviderDetailsType3 from '../../components/providerDetailsType3/providerDetailsType3';
import ProviderDetailsType4 from '../../components/providerDetailsType4/providerDetailsType4';
import ProviderServiceDetails from '../../components/providerServiceDetails/providerServiceDetails';
import { ERROR_TYPES, getErrorMessage } from '../../components/errorMessages/errorMessages.constants';
import { classifyApiError } from '../../utils/apiErrorClassifier';

// Provider type sets for conditional component rendering
const PROVIDER_TYPE_1 = new Set([
  'PHYSICIAN', 'MD - PHYSICIAN', 'MD-PHYSICIAN', 'PHYSICIAN (MD) INDV', 'PHYSICIAN (MD) GROUP',
  'Z - PHYSICIAN', 'DO-PHYSICIAN OSTEOPATH', 'DO - PHYSICIAN OSTEOPATH', 'OSTEOPATH',
  'Physician/Osteopath Individual', 'STATE EMPLOYED PHYSICIANS', 'PHYSICIAN SPECIALIST PCP',
  'PSYCHOLOGIST', 'PSYCHOLOGIST (LIC/MED) (IN-ST) (IND)', 'LICENSED CLINICAL SOCIAL WORKER (LCSW)',
  'LIC CL SOCIAL WORKER (IN-ST) INDV', 'SOCIAL WORKER', 'LICENSED PROFESSIONAL COUNSELOR (LPC)',
  'LICENSED PROFESSIONAL COUNSELOR INDV', 'LICENSED PROFESSIONAL COUNSELOR GROUP',
  'LICENSED MARRIAGE & FAMILY THERAPIST LMFT', 'LICENSED MARRIAGE & FAMILY THERAPY INDV',
  'LICENSED MARRIAGE & FAMILY THERAPY GROUP', 'LICENSED ADDICTION COUNSELOR',
  'LIC INDEP SUBSTANCE ABUSE COUNS (LISAC)', 'LIC INDEP ADDICTION COUNS (FORMER LISAC)',
  'LICENSED BEHAVIORAL HEALTH PRACTITIONER', 'NON-LICENSED BEHAVIORAL HEALTH INDIVIDUAL',
  'Other – BH Practitioner', 'Other Behavioral Health Providers', 'BEHAVIORAL HEALTH PROVIDER',
  'THERAPIST', 'SPEECH/HEARING THERAPIST', 'SPEECH LANGUAGE PATHOLOGY ASSISTANT',
  'SPEECH/LANGUAGE THERAP (IN-ST) INDV', 'PHYSICAL THERAPIST', 'PHYSICAL THERAPIST (IN-ST) INDV',
  'PHYSICAL THERAPIST (IN-ST) GROUP', 'OCCUPATIONAL THERAPIST', 'OCCUPATIONAL THERAPIST (IN-ST) INDV',
  'OCCUPATIONAL THERAPIST (IN-ST) GROUP', 'AUDIOLOGIST', 'AUDIOLOGIST (IN-ST) INDV',
  'DENTIST', 'DENTIST (IND)', 'DENTIST - ORAL SURGEON', 'DENTAL HYGIENIST',
  'OPTOMETRIST', 'OPTOMETRIST (IND)', 'OPTOMETRIST (GP)', 'OPTICIAN',
  'PODIATRIST', 'PODIATRIST (IND)', 'CHIROPRACTOR', 'CHIROPRACTOR (IND)',
  'ACUPUNCTURIST', 'NATUROPATHIC PHYSICIAN', 'GENETIC COUNSELOR', 'NUTRITIONIST',
  'REGISTERED DIETICIAN', 'REGISTERED DIETICIAN (IN-ST)', 'OPH REGISTERED DIETITIAN',
  'DIABETIC EDUCATOR', 'LACTATION SUPPORT PROVIDER', 'NURSE', 'INDEPENDENT RN',
  'NURSE (PRIVATE-RN/LPN)', 'ADVANCE PRACTICE NURSE', 'REGISTERED NURSE PRACTITIONER',
  'NURSE PRACTITIONER INDV', 'NURSE PRACTITIONER GROUP', 'CLINICAL NURSE SPECIALIST',
  'CERTIFIED NURSE-MIDWIFE', 'CERTIFIED NURSE MIDWIFE INDV', 'LICENSED MID-WIFE',
  'NURSE MIDWIFE', 'CERTIFIED REGISTERED NURSE ANESTHETIST', 'CERT REG NURS ANEST INDV',
  'PHYSICIAN ASSISTANT', 'PHYSICIAN ASSISTANT INDV', 'SURGICAL FIRST ASSISTANT',
  'ANESTHESIOLOGIST ASSISTANT', 'PERFUSIONIST', 'CERTIFIED BEHAVIOR ANALYST',
  'BOARD CERTIFIED BEHAVIOR ANALYST', 'PEER SUPPORT SPECIALIST', 'CASE MANAGER',
  'CASE MANAGER (TARGETED)', 'Z - Child and Adolescent Needs and Strengths (CANS) Provider',
  'Waivered Services Individual', 'Mid-Level Practitioner'
]);

const PROVIDER_TYPE_2 = new Set([
  'HOSPITAL', 'General Acute Care Hospital', 'LONG TERM ACUTE CARE HOSPITAL',
  'PSYCHIATRIC HOSPITAL', 'Psychiatric Hospitals', 'Inpatient Psychiatric Facility',
  'MENTAL HLTH HOSP (FREE-STAND)', 'DIST PART PSYCH HOSP (IN-ST)', 'SPECIALITY PER DIEM HOSPITALS'
]);

const PROVIDER_TYPE_3 = new Set([
  'BEHAVIORAL HEALTH RESIDENTIAL', 'THERAPEUTIC GROUP HOME', 'BOARDING HOME',
  'HABILITATION PROVIDER', 'SKILLED NURSING FACILITY', 'ADULT RESIDENTIAL CARE FAC',
  'RESID TRTMENT CTR-NON-SECURE (1-16 BEDS)', 'RESID TRTMENT CTR-SECURE (17+BEDS) (IMD)',
  'RESID TRTMENT CTR-SECURE (17+BEDS)(IMD)', 'SUBACUTE FACILITY (1-16 BEDS)',
  'SUBACUTE FACILITY (17+BEDS)(IMD)', 'SUBACUTE FACILITY (17+ BEDS) (IMD)',
  'PSYCHIATRIC RESIDENTIAL TREATMENT FACILITY GROUP', 'Psychiatric Residential Treatment Facility',
  'FOSTER CARE AGENCY', 'ADULT FOSTER CARE', 'ASSISTED LIVING CENTER',
  'ASSISTED LIVING HOME (FORMERLY ACH)', 'NURSING HOME', 'NURSING FACILITY (IN-ST)',
  'EXTENDED CARE FACILITY', 'ICF/DD REHABILITATION', 'HOME HEALTH AGENCY',
  'HOME HEALTH AGENCY (IN-ST)', 'NON-MEDICARE CERTIFIED HOME HTH AGENCIES',
  'HOSPICE', 'HOSPICE SERVICES (IN-ST)', 'BH OUTPATIENT CLINIC',
  'COMMUNITY MENTAL HEALTH CENTER/CLINIC', 'Community Mental Health Services',
  'Community Mental Health Center SUD', 'COMM MENTAL HLTH CTR/PART HOSP', 'INTEGRATED CLINICS',
  'CLINIC', 'HOSPITAL AFFILIATED CLINIC', 'PHYSICAL CLINIC', 'MENTAL HEALTH CLINIC (IN-ST)',
  'COUNSELING ONLY FACILITY', 'DIALYSIS CLINIC', 'END-STAGE RENAL DISEASE (RSD) CLINIC',
  'HEMODIALYSIS CENTER (IN-ST)', 'FREE STANDING ED', 'URGENT CARE CENTER',
  'FREE-STANDING BIRTHING CENTER', 'AMBULATORY SURGICAL CENTER', 'Ambulatory Surgical Center (ASC)',
  'AMBULATORY SURGI CTR (IN-ST)', 'LABORATORY', 'INDEPENDENT LAB',
  'INDEPENDENT TESTING FACILITIES', 'MOBILE X-RAY/IDTF', 'MOBILE XRAY/RADIATION THRPY CT',
  'PHARMACY', 'VISION CENTER', 'FEDERALLY QUALIFIED HEALTH CENTER (FQHC)',
  'FED QUALIFIED HLTH CTR (IN-ST)', 'RURAL HLTH CL(INDEPEND)(IN-ST)',
  'RURAL HLTH CL(PROV-BSE)(IN-ST)', 'COMMUNITY/RURAL HEALTH CENTER',
  'Federally Qualified Health Centers/Rural Health Clinics',
  'SCHOOL BSED HEALTH CTR (IN-ST) INDV', 'AMERICAN INDIAN/638 FACILITY',
  'SUBST USE RESIDENT TX FAC', 'RURAL SUBSTANCE ABUSE TRANSITIONAL AGCY',
  'COMMUNITY SERVICE AGENCY', 'BEHAVIORAL HEALTH THERAPEUTIC HOME', 'ASSERTIVE COMM TREAT TEAM',
  'MENTAL REHAB AGENCY (IN-ST)', 'COMMUNITY MENTAL HEALTH CENTER SUD',
  'SUBS/ALCOH ABSE CTR (X-OVERS)', 'DME PROVIDER GROUP', 'SERVICE LOCATION', 'SERVICE LOCATIONS',
  'GROUP OF PROVIDERS'
]);

const PROVIDER_TYPE_4 = new Set([
  'Doulas', 'Certified Doula', 'Doula Agency', 'Doula Individual', 'Community Doula',
  'Doula', 'Doula Services', 'DME SUPPLIER', 'DME/MEDICAL SUPPLY DEALER', 'DME PROVIDER GROUP',
  'ATTENDANT CARE', 'NON-AGENCY PERSONAL CARE AID', 'PERSONAL CARE SERVICES (IN-ST)',
  'HOME DELIVERED MEALS', 'RESPITE CARE', 'IHR-INDIVIDUAL HOME RESPITE', 'ADULT DAY HEALTH',
  'WAIVER CARE COORDINATION', 'Waiver Services', 'Waivered Services Organization',
  'Waivered Services Individual', 'EPD HCBS', 'Intensive Home Based Treatment',
  'Intensive Care Coordination ICC and Moderate Care Coor MCC', 'AIR TRANSPORTATION',
  'NON-EMERGENCY TRANSPORTATION PROVIDERS', 'EMERGENCY TRANSPORTATION',
  'AMBULANCE TRANSPORTATION', 'TRANSPORTATION PROVIDER', 'NEMT-TNC TRANSPORTATION NETWORK COMPANY',
  'TREAT & REFER', 'FISCAL INTERMEDIARIES', 'OTHER SERVICE PROVIDER',
  'BEHAVIOR INTERVENTION', 'PRESCRIBING ONLY PROVIDER'
]);

// Providers whose specialty is Attendant Care must always be treated as Type 4,
// regardless of whatever providerType they'd otherwise slot into (Type 1/2/3) — this
// takes priority over the providerType-based checks below. Kept as its own dedicated
// check (in addition to, not replacing, the original specialty[0]-based check below)
// because the original only ever looked at specialty[0].specialtyName with an
// exact-case match against PROVIDER_TYPE_4, which:
//   1) only ever checked the FIRST specialty entry, so a provider whose Attendant Care
//      specialty appeared 2nd+ in their specialty list was missed entirely.
//   2) required an exact-case match ("ATTENDANT CARE"), so any differently-cased
//      variant the API returns (e.g. "Attendant Care") silently failed to match.
const isAttendantCareSpecialty = (specialty) => {
  if (!Array.isArray(specialty)) return false;
  return specialty.some(spec => spec?.specialtyName?.trim().toUpperCase() === 'ATTENDANT CARE');
};

const getProviderDetailsType = (providerType, specialty) => {
  // Original check, preserved as-is: guarded with `specialty?.[0]` (rather than the
  // unguarded `specialty[0]` this previously crashed on) so a provider with no
  // specialty at all no longer throws here. Made case-insensitive for the same
  // reason as the providerType checks below — specialtyName casing is inconsistent
  // across plan feeds.
  const matchesSet = (set, value) => {
    if (!value) return false;
    const normalized = value.trim().toUpperCase();
    for (const entry of set) {
      if (entry.toUpperCase() === normalized) return true;
    }
    return false;
  };

  // Any specialty in the list matching PROVIDER_TYPE_4 makes this a Type 4 provider — not
  // just the first entry (specialty[0]), which previously missed a Type 4 specialty
  // appearing 2nd or later in the list.
  if (Array.isArray(specialty) && specialty.some(spec => matchesSet(PROVIDER_TYPE_4, spec?.specialtyName))) return 4;
  if (isAttendantCareSpecialty(specialty)) return 4;
  if (!providerType) return 1;

  // Case-insensitive membership check. The PROVIDER_TYPE_2/3/4 sets above were
  // populated over time from several different MCS plan feeds, so the exact casing
  // of a given providerType value is inconsistent across environments/plans (e.g.
  // 'HOSPITAL' vs 'General Acute Care Hospital' vs a plan sending plain 'Hospital').
  // A straight `Set.has(providerType)` is case-sensitive, so a value like "Hospital"
  // (title case) silently failed to match the 'HOSPITAL' entry and fell all the way
  // through to the Type 1 default — even though it's unambiguously a Type 2 hospital.
  // Normalizing both sides to uppercase before comparing fixes this while still
  // supporting every existing entry regardless of how it was originally cased.
  if (matchesSet(PROVIDER_TYPE_2, providerType)) return 2;
  if (matchesSet(PROVIDER_TYPE_3, providerType)) return 3;
  if (matchesSet(PROVIDER_TYPE_4, providerType)) return 4;
  return 1; // Type 1 is the default (includes all Type 1 values and unknowns)
};

// Security function to sanitize phone numbers for tel: links
const sanitizePhoneForHref = (phone) => {
  if (!phone || typeof phone !== 'string') return null;
  
  // Remove all whitespace and common formatting characters
  const cleaned = phone.replace(/[\s\(\)\-\.]/g, '');
  
  // Validate that it contains only digits and optional + prefix
  // This prevents any protocol injection or malicious content
  const phoneRegex = /^\+?[0-9]{7,15}$/;
  
  if (!phoneRegex.test(cleaned)) {
    return null; // Invalid phone number format
  }
  
  return cleaned;
};

// Function to calculate approximate distance based on ZIP code proximity
const calculateDistance = (provider) => {
  // If provider has distanceInMiles from API, use that
  if (provider.distanceInMiles !== undefined && provider.distanceInMiles !== null) {
    return provider.distanceInMiles;
  }
  
  // If provider has no ZIP code, return Infinity to push to end when sorting
  if (!provider.provPhyZip || provider.provPhyZip === '') {
    return Infinity;
  }
  
  const userZip = 32073; // Jacksonville area base ZIP
  const providerZip = parseInt(provider.provPhyZip) || 32000;
  
  // Simple ZIP-based distance approximation 
  const zipDifference = Math.abs(userZip - providerZip);
  
  // Create a consistent seed for this provider to avoid random distances changing on re-render
  const providerId = provider.provId || `${provider.firstName}_${provider.lastName}`;
  let seed = 0;
  for (let i = 0; i < providerId.length; i++) {
    seed += providerId.charCodeAt(i);
  }
  if (provider.provPhyZip) {
    seed += provider.provPhyZip.charCodeAt(0) * 7;
  }
  const seededRandom = ((seed % 80) + 10) / 100; // Value between 0.1 and 0.9
  
  let estimatedDistance;
  
  if (zipDifference === 0) {
    estimatedDistance = seededRandom * 10; // Same ZIP, 0-10 miles
  } else if (zipDifference <= 50) {
    estimatedDistance = seededRandom * 25 + zipDifference * 0.1; // Close ZIP codes
  } else if (zipDifference <= 200) {
    estimatedDistance = seededRandom * 50 + zipDifference * 0.2; // Medium distance
  } else {
    estimatedDistance = seededRandom * 100 + zipDifference * 0.3; // Far ZIP codes
  }
  
  // For QA purposes, reduce all distances to 0.01 of their calculated value
  return estimatedDistance * 0.1;
};
import { mockProviderSearchResponse } from '../../data/mockData.js';
import { mockSearchResultsOrg } from '../../data/mockSearchResults_org.js';
import { dummyPaSearchResponse, dummyPaFacetsResponse } from '../../data/dummyPaData.js';
import { isLocalDevMode } from '../../utils/devMode.js';
import { fetchFacets, parseFacets } from '../../services/facetsService';
import './styles.css';
import downArrow from '../../images/arrowDown.svg';

const Results = ({ envPlanCode = '' } = {}) => {
  // Set a descriptive, page-specific document title (was previously the static "Find Care"
  // from index.html, identical to the Search/"Find Care" page's title — screen reader users
  // and browser tab/history/bookmark lists had no way to distinguish the two pages).
  useEffect(() => {
    document.title = 'Search Results | Aetna Better Health';
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Phase 5 (URL Parameter Removal — Browser Navigation Support): react-router's own location
  // object, specifically for its `.state`/`.key` — NOT for `.search`/query-string reading
  // (that's Phase 2's `searchParams`, above, used only for the one-time hydration effect).
  // Aliased at import-time to avoid colliding with the app's own `useLocation` from
  // LocationContext (below) — unlike Phase 2's `routerLocation`, which was dead code and
  // removed, this one is genuinely used (see the popstate-replacement effect further down).
  const routerLocation = useRouterLocation();
  const { userLocation, updateUserLocation } = useLocation();
  const userLocationRef = useRef(userLocation);
  userLocationRef.current = userLocation;
  const updateUserLocationRef = useRef(updateUserLocation);
  updateUserLocationRef.current = updateUserLocation;
  const { getSearchResultFields, planFeatures, environmentId, loading: planFeaturesLoading, error: planFeaturesError, getCentralZip, setPlanCode, getFooterLinks, getErrorContactUsUrl } = usePlanFeatures();
  // Akorbi (crestTranslator) only scans/translates whatever's in the DOM at the moment
  // start() is called. TranslationContext calls start() once on initial app load and again
  // whenever the user explicitly switches languages — but it has no idea when THIS page's
  // results list (loaded asynchronously, well after either of those triggers) shows up in
  // the DOM. That's why switching to Spanish on Search, then navigating to Results, left
  // the newly-rendered provider cards untranslated. Re-run start() here whenever the
  // results list (re)populates while a non-English language is active.
  const { selectedLanguage, retranslateContent } = useTranslation();
  // Phase 2 (URL Parameter Removal): all search criteria (query, filters, sort, paging,
  // selected plan, location) now live in SearchStateContext instead of the URL. See the
  // hydration effect below for the one-time "read URL, populate context, clean URL" step.
  const { searchState, updateSearchState, updateFilters, batchUpdateFilters } = useSearchState();

  // Resolve the environment plan code (e.g. "FL"): prefer the path-sourced prop passed down
  // from FindCareResults in App.jsx (Phase 0). Fall back to the legacy `?plan=` query param
  // for the bare /results route (guarded by RequiresPlan).
  const resolvedEnvPlan = envPlanCode || searchParams.get('plan') || '';

  // On direct load/refresh, search.jsx never runs so plan features never get fetched.
  // Mirror search.jsx's synchronous setPlanCode call to kick off the fetch here too.
  if (resolvedEnvPlan && !planFeatures) {
    setPlanCode(resolvedEnvPlan);
  }

  // Track if the one-time URL hydration has run (StrictMode-safe self-guard for the effect
  // below). This is intentionally a ref, not state — it only needs to be read/written
  // synchronously within that single effect's own body.
  const initialLoadRef = useRef(false);
  // Separate STATE flag (not a ref) that gates the context-watching search effect further
  // down. This has to be real state — not just initialLoadRef — because the hydration
  // effect's updateSearchState() call doesn't apply synchronously; if the watcher effect
  // gated on the ref alone, it could fire once against the stale/default context (same
  // commit, before the hydrated state lands) and then again once hydration actually
  // applies, causing a wasted/incorrect duplicate fetch on first load.
  const [hydrated, setHydrated] = useState(false);
  // Track if a fetch is already running to prevent overlapping duplicate requests
  const initialSearchPerformedRef = useRef(false);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalResults, setTotalResults] = useState(0);
  const [expandedStates, setExpandedStates] = useState({});
  // Bumped on every new search to remount <ProviderFilters> and clear its internal state.
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  // Re-trigger Akorbi translation once the results list finishes loading. Without this,
  // a language selected on a prior page (e.g. Search) never gets applied to this page's
  // async-loaded provider cards — see the comment above the useTranslation() call for why.
  // Uses TranslationContext's retranslateContent() (not window.crestTranslator.start()
  // directly) so this shows the same "Translation is in progress..." overlay used for
  // explicit language swaps, rather than a raw, jarring flash of English text suddenly
  // flipping to the selected language on every single results load.
  useEffect(() => {
    if (loading) return; // wait until the new results have actually landed in the DOM
    retranslateContent();
  }, [results, loading, selectedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Move focus to the results summary heading ("X results for ...") once results finish
  // loading, instead of leaving focus wherever it landed on navigation (typically the top
  // of the page, before the search bar/filters). Testers reported that after searching for
  // a super category and landing on this page, it wasn't obvious anything had actually
  // happened — the results themselves render further down and required scrolling to find,
  // with no visual or focus cue pointing at them. Fires on every fresh results load (initial
  // load, new search, and pagination alike) — gated on `hydrated` so it never fires before
  // the one-time URL hydration effect has even had a chance to kick off the first search.
  // Experimental — revisit based on tester feedback.
  useEffect(() => {
    if (loading || !hydrated) return;
    const heading = document.getElementsByClassName('provider-filters-title');
    if (heading) {
      heading[0].focus({ preventScroll: true });
      heading[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [results, loading, hydrated]);


  // --- Search/filter/paging criteria — sourced from SearchStateContext (Phase 2) ---
  // These are plain consts, refreshed every render, rather than separate useState values.
  // Everything below this point that reads `selectedPlan`, `filters`, `currentPage`,
  // `resultsPerPage`, `searchQuery`, `searchDisplayQuery`, or `sortOption` keeps working
  // exactly as before — only *how* these values are produced has changed (derived from
  // context instead of independently-managed local state that had to be kept in sync by hand).
  const { selectedPlan, filters, currentPage, resultsPerPage, superCategory, subCategory, popularSearchId, psLabel, query } = searchState;

  // Effective search term sent to the API: popular-search id, then browse-by category,
  // then the raw typed query (same precedence the old per-effect `searchTerm` used).
  // IMPORTANT: this deliberately stays superCategory-based (never subCategory) even when
  // both are set — fetchResults' subCategory branch further below relies on this value
  // holding the PARENT category text so it can correctly populate `searchBody.superCategory`
  // alongside `searchBody.subCategory`. Use `searchDisplayQuery` below for anything
  // user-facing instead of reaching for this directly.
  const searchQuery = popularSearchId || superCategory || query;
  // Human-readable label for display (results header, print, email, and the search bar's
  // initialValue). Previously a separately-managed `searchDisplayQuery` state that every
  // handler had to remember to update by hand — `handleNewSearch` never did, which meant
  // the results header kept showing a stale popular-search label after typing a brand-new
  // search. Deriving it fresh every render fixes that for free.
  //
  // Prefers `subCategory` over `searchQuery` (which, per the note above, resolves to
  // `superCategory` for a browse-by drill-down) so a subcategory selection (e.g. "Family
  // Medicine" drilled into from "Primary Care") displays its own, more specific label rather
  // than its parent's — in the results summary, the search bar, print, and email — instead
  // of the super category being shown everywhere as if it were the actual search term. This
  // only affects what's DISPLAYED; the actual API request already correctly sends both
  // `subCategory` and `superCategory` as distinct fields (see fetchResults below), so no
  // change was needed there.
  const searchDisplayQuery = (popularSearchId && psLabel) ? psLabel : (subCategory || searchQuery);
  // Sort is folded into filters.sort in context (see SearchStateContext DEFAULT_FILTERS) —
  // aliased here so the render/useMemo code below reads exactly as it did before.
  const sortOption = filters.sort;

  // Facets state - stores available filter options from API
  const [facets, setFacets] = useState(null);
  const [facetsLoading, setFacetsLoading] = useState(false);
  const [facetsError, setFacetsError] = useState('');

  // Location coordinates state - Initialize with null to detect when not yet loaded
  const [userCoordinates, setUserCoordinates] = useState({
    latitude: null,
    longitude: null,
    source: 'uninitialized'
  });

  // Location display string for showing user's location
  const [locationDisplay, setLocationDisplay] = useState('');

  const [locationFallbackNeeded, setLocationFallbackNeeded] = useState(false);
  const locationFallbackExecutedRef = useRef(false);

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Incorrect info modal state
  const [showIncorrectInfoModal, setShowIncorrectInfoModal] = useState(false);
  const [selectedProviderForFeedback, setSelectedProviderForFeedback] = useState(null);

  // Distance panel state
  const [isDistancePanelOpen, setIsDistancePanelOpen] = useState(false);

  // Get user's location on mount (for search with coordinates)
  // Priority: URL params (hydrated into context once, see below) > localStorage cache >
  // Browser geolocation > IP-based > Default FL
  //
  // NOTE (Phase 2 — URL Parameter Removal): this used to depend on `[searchParams]` and
  // re-read `searchParams.get('lat'/'lon')` directly, so it was scheduled to re-run this
  // ENTIRE cascade (including the browser geolocation prompt path) on every single filter
  // change, since those used to call setSearchParams too. It happened to mostly no-op fast
  // thanks to the 5-minute location cache check below, but that was incidental, not by
  // design. lat/lon are now read from the URL exactly once during the hydration effect
  // further down and stored in context, so this effect correctly runs once, after hydration.
  useEffect(() => {
    if (!hydrated) return;
    const getLocationWithFallback = async () => {
      const rawUserLocation = userLocationRef.current;
      const updateUserLocation = updateUserLocationRef.current;
      // Only trust a cached context location if it was actually resolved for the state/plan
      // we're viewing right now (resolvedEnvPlan, from the URL path — the only "which state"
      // signal available synchronously here; PlanFeaturesContext's environmentId only
      // resolves after an async fetch, too late to gate this). Otherwise treat this pass
      // exactly as if there were no cached location at all, so every check below
      // (opportunistic display-string lookup, the 5-minute cache-reuse branch, the "preserve
      // existing details" spread) falls through to fresh geolocation/IP/central-zip detection
      // instead of silently reusing — or worse, actually searching against — a different
      // state's coordinates. This is what "changing states" (there's no in-app UI to switch
      // states, so it always means loading a different /find-care/:planCode URL in the same
      // browser tab/session) needs: a sessionStorage-cached location from a PREVIOUS state is
      // read straight back in by LocationContext's lazy useState(readStoredLocation)
      // initializer on this fresh mount, before anything else validates it. Untagged
      // (legacy/pre-fix) cached locations are also treated as a mismatch — safe, since it only
      // costs one redundant detection the first time; every location resolved below is tagged
      // with planCode: resolvedEnvPlan so future loads compare correctly.
      const userLocation = (rawUserLocation && rawUserLocation.planCode === resolvedEnvPlan) ? rawUserLocation : null;
      // 1. Try to get coordinates hydrated from the URL first (from location panel deep link)
      const latParam = searchState.latitude;
      const lonParam = searchState.longitude;
      
      if (latParam && lonParam) {        const urlCoords = {
          latitude: latParam,
          longitude: lonParam,
          source: 'url'
        };
        setUserCoordinates(urlCoords);
        
        // Check context for location details to get display string
        let displaySet = false;
        // Tracks whether THIS pass already wrote a full (formattedAddress-bearing) location
        // to context, so the "update context if not already done" block further down can't
        // re-fire on top of it. Needed because `userLocation` above is a snapshot captured
        // once at the top of this async function (via the ref) — it never reflects updates
        // made later in this SAME pass, so checking it again after the reverse-geocode call
        // below used to look stale/empty even immediately after that call had just set it,
        // causing the second block to "helpfully" re-save a bare {latitude, longitude} object
        // over the good one. Since LocationContext's updateUserLocation does a full replace
        // (not a merge), that silently erased the formattedAddress/city/stateCode that were
        // just resolved — which is what made the location appear to "come up different" or
        // vanish entirely after a refresh followed by navigating back to search.
        let contextUpdatedThisPass = false;
        if (userLocation) {
          if (userLocation.formattedAddress) {
            setLocationDisplay(userLocation.formattedAddress);
            displaySet = true;
          } else if (userLocation.city && userLocation.stateCode) {
            const displayString = userLocation.zipCode 
              ? `${userLocation.city}, ${userLocation.stateCode} ${userLocation.zipCode}`
              : `${userLocation.city}, ${userLocation.stateCode}`;
            setLocationDisplay(displayString);
            displaySet = true;
          }
        }
        
        // If no display string from context, try to reverse geocode the coordinates
        if (!displaySet) {
          try {
            const locationData = await locationService.reverseGeocode(urlCoords.latitude, urlCoords.longitude);
            if (locationData && locationData.formattedAddress) {
              setLocationDisplay(locationData.formattedAddress);
              displaySet = true;
              
              // Update context with full location details
              const updatedLocation = {
                ...urlCoords,
                city: locationData.city,
                stateCode: locationData.stateCode,
                zipCode: locationData.zipCode,
                formattedAddress: locationData.formattedAddress,
                planCode: resolvedEnvPlan, // Track which state/plan this location belongs to
                timestamp: Date.now()
              };
              if (updateUserLocation) {
                updateUserLocation(updatedLocation);
                contextUpdatedThisPass = true;
              }
            }
          } catch (error) {
            console.warn('⚠️ Failed to reverse geocode URL coordinates:', error);
          }
        }
        
        if (!displaySet) {
          setLocationDisplay('Unknown location');
        }
        
        // Update context with URL coords if not already done (this pass, or in a prior pass
        // reflected by the current context already having a formattedAddress).
        if (displaySet && !contextUpdatedThisPass && !userLocation?.formattedAddress) {
          const updatedLocation = {
            ...urlCoords,
            ...(userLocation || {}), // Preserve existing location details if any
            latitude: urlCoords.latitude,
            longitude: urlCoords.longitude,
            planCode: resolvedEnvPlan, // Track which state/plan this location belongs to
            timestamp: Date.now()
          };
          
          if (updateUserLocation) {
            updateUserLocation(updatedLocation);
          }
        }
        return;
      }

      // 2. Check context for previously stored location
      if (userLocation && userLocation.timestamp) {
        const location = userLocation;
        const age = Date.now() - location.timestamp;
        // Use cached location if less than 5 minutes old
        if (age < 300000) {          setUserCoordinates({
            latitude: location.latitude,
            longitude: location.longitude,
            source: 'cache'
          });
          
          // Set location display string - check for valid values first
          let displayString = 'Unknown location';
          if (location.formattedAddress) {
            displayString = location.formattedAddress;
          } else if (location.city && location.stateCode) {
            displayString = location.zipCode
              ? `${location.city}, ${location.stateCode} ${location.zipCode}`
              : `${location.city}, ${location.stateCode}`;
          }
          setLocationDisplay(displayString);
          
          return;
        }
      }
      
      // 3. Try browser geolocation API
      if ('geolocation' in navigator) {        navigator.geolocation.getCurrentPosition(
          async (position) => {            const coords = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              source: 'browser'
            };
            setUserCoordinates(coords);
            
            // Try to reverse geocode to get location display
            try {
              const locationData = await locationService.reverseGeocode(coords.latitude, coords.longitude);
              if (locationData && locationData.formattedAddress) {
                // Guard against browser geolocation resolving to a location OUTSIDE the
                // current plan's state (e.g. a PA-plan session where the device/network's
                // actual location — or a VPN/proxy exit point — is in California). Without
                // this check, that out-of-state address would be accepted and persisted
                // (tagged planCode: resolvedEnvPlan) same as any legitimate in-state
                // address, and every later staleness check only compares planCode, never
                // the actual state, so it would never get caught. On a mismatch, skip
                // storing this location and fall through to the IP-based/central-zip
                // fallbacks below instead of trusting the out-of-state coordinates.
                const expectedState = resolveExpectedStateCode(resolvedEnvPlan, planFeatures);
                if (!isLocationInExpectedState(locationData, expectedState)) {
                  console.warn('⚠️ Browser geolocation resolved outside plan state, ignoring and falling back:', {
                    resolvedState: locationData.stateCode,
                    expectedState
                  });
                } else {
                  setLocationDisplay(locationData.formattedAddress);

                  // Update context with full location details
                  const fullLocationData = {
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                    city: locationData.city,
                    stateCode: locationData.stateCode,
                    zipCode: locationData.zipCode,
                    formattedAddress: locationData.formattedAddress,
                    planCode: resolvedEnvPlan, // Track which state/plan this location belongs to
                    timestamp: Date.now(),
                    source: 'browser'
                  };
                  if (updateUserLocation) {
                    updateUserLocation(fullLocationData);
                  }
                  return;
                }
              }
            } catch (error) {
              console.warn('⚠️ Failed to reverse geocode browser location:', error);
            }
            
            // If reverse geocode failed OR resolved outside the plan's state, don't fall back
            // to bare, unvalidated coordinates either — go straight to the IP-based/
            // central-zip fallbacks below instead (see the IP-based branch's own state check
            // just below, and the central-zip fallback effect, neither of which can produce
            // an out-of-state result the way raw device coordinates already have here).
            setLocationFallbackNeeded(true);
          },
          async (error) => {            // 4. Try IP-based geolocation as fallback
            try {              const response = await fetch('https://ipapi.co/json/');
              if (response.ok) {
                const data = await response.json();
                if (data.latitude && data.longitude) {                  const coords = {
                    latitude: data.latitude,
                    longitude: data.longitude,
                    source: 'ip'
                  };

                  // Same out-of-state guard as the browser-geolocation branch above —
                  // ipapi.co's `region_code` is its 2-letter state abbreviation (e.g. "CA"),
                  // which is the actual field that would have caught the PA-plan/CA-IP
                  // mismatch that motivated this whole check. IP geolocation is already the
                  // least trustworthy source (VPNs/corporate proxies routinely resolve to the
                  // wrong region), so skip storing it entirely on a mismatch rather than
                  // caching a location we already know is likely wrong.
                  const expectedState = resolveExpectedStateCode(resolvedEnvPlan, planFeatures);
                  const ipLocationForValidation = { stateCode: data.region_code };
                  if (!isLocationInExpectedState(ipLocationForValidation, expectedState)) {
                    console.warn('⚠️ IP geolocation resolved outside plan state, ignoring and falling back to central zip:', {
                      resolvedState: data.region_code,
                      expectedState
                    });
                    setLocationFallbackNeeded(true);
                    return;
                  }

                  setUserCoordinates(coords);
                  
                  // Update context (app memory only)
                  const locationData = {
                    latitude: coords.latitude,
                    longitude: coords.longitude,
                    stateCode: data.region_code,
                    planCode: resolvedEnvPlan, // Track which state/plan this location belongs to
                    timestamp: Date.now(),
                    source: 'ip',
                    city: data.city,
                    region: data.region
                  };
                  if (updateUserLocation) {
                    updateUserLocation(locationData);
                  }
                  return;
                }
              }
            } catch (ipError) {}

            // 5. All location detection failed — fall back to plan's central zip
            setLocationFallbackNeeded(true);
          },
          {
            enableHighAccuracy: false,
            timeout: 5000,
            maximumAge: 300000 // 5 minutes
          }
        );
      } else {
        // 4. Try IP-based geolocation if browser geolocation not available
        try {
          const response = await fetch('https://ipapi.co/json/');
          if (response.ok) {
            const data = await response.json();
            if (data.latitude && data.longitude) {
              const coords = {
                latitude: data.latitude,
                longitude: data.longitude,
                source: 'ip'
              };

              // Same out-of-state guard as the other IP-based branch above.
              const expectedState = resolveExpectedStateCode(resolvedEnvPlan, planFeatures);
              const ipLocationForValidation = { stateCode: data.region_code };
              if (!isLocationInExpectedState(ipLocationForValidation, expectedState)) {
                console.warn('⚠️ IP geolocation resolved outside plan state, ignoring and falling back to central zip:', {
                  resolvedState: data.region_code,
                  expectedState
                });
                setLocationFallbackNeeded(true);
                return;
              }

              setUserCoordinates(coords);

              // Update context (app memory only)
              const locationData = {
                latitude: coords.latitude,
                longitude: coords.longitude,
                stateCode: data.region_code,
                planCode: resolvedEnvPlan, // Track which state/plan this location belongs to
                timestamp: Date.now(),
                source: 'ip'
              };
              if (updateUserLocation) {
                updateUserLocation(locationData);
              }
              return;
            }
          }
        } catch (ipError) {}

        // 5. All location detection failed — fall back to plan's central zip
        setLocationFallbackNeeded(true);
      }
    };

    getLocationWithFallback();
    // Runs once after hydration completes (see comment above) — no longer tied to
    // searchParams, which after Phase 2 only ever changes once (the initial mount clean-up).
  }, [hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Central zip fallback: runs when geolocation/IP both fail, waiting for planFeatures to load
  useEffect(() => {
    if (!locationFallbackNeeded || !planFeatures || locationFallbackExecutedRef.current) return;
    locationFallbackExecutedRef.current = true;

    const runCentralZipFallback = async () => {
      try {
        // Local dev mode has no Google Maps API key (see search.jsx/resultsSummary.jsx's
        // "API key not found" warnings), so locationService.geocodeGoogle() below always
        // times out after 5s and throws — which fell into the catch block, permanently
        // setting userCoordinates to { latitude: null, longitude: null }. The main
        // fetchResults-trigger effect requires userCoordinates.latitude/longitude to be
        // truthy before it will ever fetch (dummy PA data included), so on localhost the
        // "fauxsults" never actually rendered — this fallback never resolved to anything
        // fetchResults could use. Skip the Google geocode entirely in local dev and use a
        // coordinate that matches the dummyPaData.js provider set (Norristown/King of
        // Prussia, PA — ~19401/19403) instead, so the dummy-data search path further down
        // always has real coordinates to key off of.
        if (isLocalDevMode()) {
          const coords = { latitude: 40.1215, longitude: -75.3389, source: 'centralZip' };
          setUserCoordinates(coords);
          setLocationDisplay('Norristown, PA 19401');
          const updateUserLocation = updateUserLocationRef.current;
          if (updateUserLocation) {
            updateUserLocation({
              ...coords,
              city: 'Norristown',
              stateCode: 'PA',
              zipCode: '19401',
              formattedAddress: 'Norristown, PA 19401',
              planCode: resolvedEnvPlan,
              timestamp: Date.now(),
            });
          }
          return;
        }

        const centralZip = getCentralZip();
        if (!centralZip) {
          setUserCoordinates({ latitude: null, longitude: null, source: 'unknown' });
          setLocationDisplay('Unknown location');
          return;
        }
        // Bound this geocode to the plan's expected state (from the URL's :planCode
        // segment via resolveExpectedStateCode) — without it, geocodeGoogle's
        // Place.searchByText({textQuery: "19422"}) has no location bias/restriction
        // whatsoever yet at this point in the flow (that only gets set up later, once
        // a location already exists — see locationPanel.jsx's getCentralCoords), so a
        // bare zip-only query can match a same-numbered ZIP/place in a completely
        // different state, silently resolving this plan's OWN central zip to the
        // wrong state's coordinates and offsetting every distance/result downstream
        // from it.
        const expectedState = resolveExpectedStateCode(resolvedEnvPlan, planFeatures);
        const coordsData = await locationService.geocodeGoogle({ zip: centralZip, state: expectedState || undefined });
        const coords = {
          latitude: coordsData.latitude,
          longitude: coordsData.longitude,
          source: 'centralZip'
        };
        setUserCoordinates(coords);
        const display = coordsData.formattedAddress || `${coordsData.city}, ${coordsData.stateCode} ${centralZip}`;
        setLocationDisplay(display);
        const updateUserLocation = updateUserLocationRef.current;
        if (updateUserLocation) {
          updateUserLocation({
            ...coords,
            city: coordsData.city,
            stateCode: coordsData.stateCode,
            zipCode: centralZip,
            formattedAddress: coordsData.formattedAddress,
            planCode: resolvedEnvPlan, // Track which state/plan this location belongs to
            timestamp: Date.now(),
          });
        }
      } catch {
        setUserCoordinates({ latitude: null, longitude: null, source: 'unknown' });
        setLocationDisplay('Unknown location');
      }
    };

    runCentralZipFallback();
  }, [locationFallbackNeeded, planFeatures]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Phase 2.2: One-time URL hydration + URL clean-up (runs once on mount) ---
  // Reads every deep-link query param exactly once, pushes it into SearchStateContext, then
  // strips the URL down to the bare path. From this point on, the URL is never read from or
  // written to again for search/filter/paging state — context is the single source of truth.
  useEffect(() => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;

    const hasUrlParams = Array.from(searchParams.keys()).length > 0;

    // Only build/apply urlState if there's actually something to read. If there are no URL
    // params (plain refresh or in-app navigation with no deep link), SearchStateContext has
    // already restored the right state from sessionStorage (or sensible defaults) itself
    // during its own mount — re-parsing sessionStorage a second time here would just
    // duplicate that logic, so we simply leave it alone in that case.
    if (hasUrlParams) {
      const urlState = {
        query: searchParams.get('query') || '',
        superCategory: searchParams.get('superCategory') || '',
        subCategory: searchParams.get('subCategory') || '',
        popularSearchId: searchParams.get('ps') || '',
        psLabel: searchParams.get('psLabel') || '',
        latitude: searchParams.get('lat') ? parseFloat(searchParams.get('lat')) : null,
        longitude: searchParams.get('lon') ? parseFloat(searchParams.get('lon')) : null,
        // Note: NOT searchParams.get('plan') — that's the env code, sourced only from the
        // path (resolvedEnvPlan/envPlanCode, Phase 0). `planCode` here is the user's specific
        // selected plan (e.g. "FMMA"), a true deep-link value.
        selectedPlan: searchParams.get('planCode') || '',
        filters: {
          gender: searchParams.get('gender') || 'no-preference',
          newPatientStatus: searchParams.get('newPatientStatus') || 'no-preference',
          distance: searchParams.get('distance') || '25',
          visitType: searchParams.get('visitType') || 'no-preference',
          sort: searchParams.get('sort') || 'Distance',
          language: searchParams.get('language') ? searchParams.get('language').split(',') : [],
          specialty: searchParams.get('specialty') ? searchParams.get('specialty').split(',') : [],
          boardCertified: searchParams.get('boardCertified') || 'no-preference',
          acceptsMedicaid: searchParams.get('acceptsMedicaid') ? searchParams.get('acceptsMedicaid').split(',') : [],
          afterHoursWeekend: searchParams.get('afterHoursWeekend') ? searchParams.get('afterHoursWeekend').split(',') : [],
          groupAffiliation: searchParams.get('groupAffiliation') ? searchParams.get('groupAffiliation').split(',') : [],
          handicapAccess: searchParams.get('handicapAccess') ? searchParams.get('handicapAccess').split(',') : [],
          providerRace: searchParams.get('providerRace') ? searchParams.get('providerRace').split(',') : [],
          providerEthnicity: searchParams.get('providerEthnicity') ? searchParams.get('providerEthnicity').split(',') : [],
          birthingFriendlyCare: searchParams.get('birthingFriendlyCare') || 'no-preference',
          agesServed: searchParams.get('agesServed') || 'no-preference',
          cshcs: searchParams.get('cshcs') ? searchParams.get('cshcs').split(',') : [],
          pubTrans: searchParams.get('pubTrans') ? searchParams.get('pubTrans').split(',') : [],
          hospitalAffiliation: searchParams.get('hospitalAffiliation') ? searchParams.get('hospitalAffiliation').split(',') : [],
          specializedTraining: searchParams.get('specializedTraining') ? searchParams.get('specializedTraining').split(',') : [],
          accreditation: searchParams.get('accreditation') ? searchParams.get('accreditation').split(',') : [],
          specialProviderType: searchParams.get('specialProviderType') ? searchParams.get('specialProviderType').split(',') : []
        },
        currentPage: 1
      };

      updateSearchState(urlState);
    }

    // Clean the URL down to the bare path. resolvedEnvPlan comes from useParams() (Phase 0),
    // NOT from searchParams, so this results in ZERO query params — nothing re-adds them.
    navigate(`/find-care/${resolvedEnvPlan}/results`, { replace: true });

    // Unblock the context-watching search effect below now that hydration has landed.
    setHydrated(true);
    // Intentionally empty deps — this must run exactly once, on mount, regardless of
    // searchParams/navigate identity churn (initialLoadRef guards StrictMode's double-invoke).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Phase 5: Browser Navigation Support ---
  // handleNewSearch (below) and search.jsx's handleSearch/handlePopularSearch/
  // handleBrowseBySearch each attach the COMPLETE resulting SearchStateContext state as
  // `navigate(path, { state: { searchStateSnapshot } })` — never as a URL query string, only
  // as the invisible `state` payload React Router attaches to that browser history entry.
  // The browser already tracks these entries correctly and bidirectionally (back AND
  // forward, including multi-step jumps) on its own; we just need to read the snapshot back
  // out whenever the user lands on a different entry for this route. `routerLocation.key` is
  // a stable, unique-per-entry identifier (same key if you return to an entry already
  // visited, new key only for a genuinely new push) — cheaper and more reliable to key this
  // effect on than trying to parse a raw `popstate` DOM event by hand.
  //
  // This fires on this component's very first mount too (all effects do) — which correctly
  // handles landing here via a forward-navigation that remounts this component (e.g. the user
  // went back past this page to the search page, then forward again). On a plain fresh visit
  // with no snapshot attached (a bare Link click, or arriving from search.jsx before Phase 5
  // existed), `routerLocation.state` is simply undefined and this safely no-ops, leaving the
  // Phase 2.2 hydration effect and SearchStateContext's own sessionStorage restore as the
  // only sources of truth, exactly as before.
  //
  // NOTE: this supersedes Phase 1's original `pushToHistory`/`popFromHistory` sessionStorage
  // stack design (see SearchStateContext.jsx's Phase 5 note for why: a LIFO pop can't support
  // forward/redo). Nothing here needs those functions.
  useEffect(() => {
    const snapshot = routerLocation.state?.searchStateSnapshot;
    if (snapshot) {
      updateSearchState(snapshot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.key]);

  // --- Phase 2.5: context-watching search trigger ---
  // Replaces the old URL-watching effect. Fires whenever any search-relevant piece of
  // context changes (query, filters, paging, selected plan, popular search, browse-by
  // category) — which now happens for EVERY user action that used to write to the URL
  // (filter change, batch filter apply, pagination, results-per-page, new search, sort).
  // This is the single, sole place fetchResults is invoked from a reactive trigger — filter/
  // pagination/search handlers below just update context and let this effect do the fetch,
  // eliminating the old filterFetchActiveRef "did a handler already fetch?" guard entirely.
  useEffect(() => {
    if (!hydrated) return;

    // Skip once if a fetch is already in flight for the exact same criteria (guards against
    // React StrictMode's dev-only double-invoke of effects; harmless no-op in production).
    if (!searchQuery || !userCoordinates?.latitude || !userCoordinates?.longitude || !environmentId) {
      return;
    }

    const searchType = determineSearchType();
    // `sort` is now included in the cache key: the search API supports an optional
    // `sortBy` param (name_asc/name_desc/distance) so a sort change now needs its own
    // re-fetch (server-side sort) rather than being applied purely client-side against
    // whatever page of results happened to already be loaded — see fetchResults' searchBody
    // assembly below for the `sortOption` -> `sortBy` mapping.
    const searchKey = JSON.stringify({
      searchQuery, selectedPlan, environmentId,
      lat: userCoordinates.latitude, lon: userCoordinates.longitude,
      filters, currentPage, resultsPerPage, subCategory
    });
    if (initialSearchPerformedRef.current === searchKey) return;
    initialSearchPerformedRef.current = searchKey;

    fetchResults(searchQuery, currentPage, resultsPerPage, searchType, filters.distance, selectedPlan, filters, subCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, searchState, userCoordinates, environmentId]);

  // Determine which API param the current search term should be sent as. Previously this
  // `popularSearchId ? 'ps' : superCategory ? 'superCategory' : 'query'` ternary was
  // duplicated verbatim in 5 places (the old main effect, handleFilterChange,
  // handleBatchFilterChange, handlePageChange, handleResultsPerPageChange) — now there's
  // exactly one call site (the watcher effect above), since pagination/filter handlers no
  // longer call fetchResults directly.
  const determineSearchType = () => (popularSearchId ? 'ps' : superCategory ? 'superCategory' : 'query');

  // Handle filter changes — just update context; the watcher effect above triggers the fetch.
  // updateFilters() also resets currentPage to 1 (see SearchStateContext), matching the old
  // handler's explicit setCurrentPage(1) call.
  //
  // Also resets expandedStates (each provider card's "Provider details"/"Service details"
  // expand/collapse) back to fully collapsed. Without this, expandedStates stays keyed by
  // provId across a filter change — so a provider card left expanded before applying a
  // filter would still render expanded afterward even though the underlying results list
  // (and possibly that very provider's position/presence in it) just changed via the new
  // fetch, which looked like the previous filter sidesheet interaction's expand/collapse
  // state was incorrectly "carrying over" into the freshly-filtered results.
  const handleFilterChange = (filterType, value) => {
    updateFilters({ [filterType]: value });
    setExpandedStates({});
  };

  // Batch update multiple filters at once (for sidebar "Apply" button) — same pattern,
  // same expandedStates reset as handleFilterChange above (this is the handler the "All
  // filters" sidesheet's Apply button actually calls).
  const handleBatchFilterChange = (filtersToUpdate) => {
    batchUpdateFilters(filtersToUpdate);
    setExpandedStates({});
  };

  // Function to get plan display name from plan features
  const getPlanDisplayName = () => {
    if (!selectedPlan) return '';
    
    // API returns planType for long names, planCode for codes
    // Match the selected plan code to find the display name
    const planTypes = planFeatures?.planType || planFeatures?.planName || [];
    const planCodes = planFeatures?.planCode || [];
    
    if (Array.isArray(planTypes) && Array.isArray(planCodes)) {
      const planIndex = planCodes.findIndex(code => code === selectedPlan);
      if (planIndex !== -1 && planTypes[planIndex]) {
        return planTypes[planIndex];
      }
    }
    
    // Enhanced fallback formatting for common plan types
    const planDisplayNames = {
      'FLTC': 'Florida - Long Term Care',
      'FHK': 'Florida Healthy Kids', 
      'FMMA': 'Florida Medicaid - MMA',
      'florida_long_term_care': 'Florida – Long Term Care',
      'florida_healthy_kids': 'Florida Healthy Kids', 
      'florida_medicaid_mma': 'Florida Medicaid – MMA',
      'long_term_care': 'Long Term Care',
      'healthy_kids': 'Healthy Kids', 
      'medicaid_mma': 'Medicaid - MMA',
      'medicaid': 'Medicaid',
      'chip': 'CHIP',
      'single_line': 'Single Line of Business'
    };
    
    if (planDisplayNames[selectedPlan]) {
      return planDisplayNames[selectedPlan];
    }
    
    // Final fallback to format the selected plan value
    return selectedPlan
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Shared builder for the provider search request body — used both by fetchResults()
  // (the live search driving what's rendered on screen) AND handleEmailResults() below.
  // Previously handleEmailResults built its own minimal, separate body containing only
  // envId/zip/query/page/size/radius — missing lat/long, planCode, sortBy, and every
  // active filter (specialty, gender, language, boardCertification, etc.), and always
  // sending `query` even when the current search was actually a popular-search (`psId`)
  // or browse-by category (`superCategory`/`subCategory`). Since the digital-deliveries
  // email endpoint re-runs the search server-side using whatever providerSearchRequest
  // it's given, that stripped-down body made it re-search without geo-coordinates or any
  // of the user's applied filters — returning a different (often smaller or differently
  // ordered) set of providers than what's actually shown on the current results page, so
  // the emailed PDF didn't match/include all the providers on screen. Building both
  // requests from this single function keeps them in sync going forward.
  const buildProviderSearchBody = (query, page = 1, pageSize = resultsPerPage, paramType = 'query', overrideDistance = null, overridePlanCode = null, overrideFilters = null, subCategory = null) => {
    const availablePlanCodes = planFeatures?.planCode || [];
    let planCode = overridePlanCode || selectedPlan;
    if (planCode && (availablePlanCodes.length === 0 || !availablePlanCodes.includes(planCode))) {
      planCode = '';
    }

    const apiSize = Math.min(pageSize, 100);
    const activeFilters = overrideFilters || filters;

    const searchBody = {
      envId: environmentId,
      page: page - 1,
      size: apiSize,
      lat: userCoordinates.latitude,
      long: userCoordinates.longitude,
      radius: overrideDistance || activeFilters.distance || '25',
      ...(planCode && { planCode })
    };

    if (activeFilters.sort === 'Name (A–Z)') {
      searchBody.sortBy = 'name_asc';
    } else if (activeFilters.sort === 'Name (Z–A)') {
      searchBody.sortBy = 'name_desc';
    }

    if (activeFilters.gender && activeFilters.gender !== 'no-preference') {
      searchBody.gender = activeFilters.gender === 'male' ? 'Male' :
                         activeFilters.gender === 'female' ? 'Female' :
                         activeFilters.gender.charAt(0).toUpperCase() + activeFilters.gender.slice(1).toLowerCase();
    }

    if (activeFilters.newPatientStatus === 'accepting') {
      searchBody.newPatientStatus = ['Accepting new patients'];
    }

    if (activeFilters.boardCertified) {
      const bcValue = Array.isArray(activeFilters.boardCertified)
        ? (activeFilters.boardCertified.includes('yes') ? 'yes'
          : activeFilters.boardCertified.includes('no') ? 'no'
          : null)
        : activeFilters.boardCertified;
      if (bcValue === 'yes') {
        searchBody.boardCertification = true;
      } else if (bcValue === 'no') {
        searchBody.boardCertification = false;
      }
    }

    if (activeFilters.language && Array.isArray(activeFilters.language) && activeFilters.language.length > 0) {
      searchBody.languagesSpoken = activeFilters.language;
    }

    if (activeFilters.visitType && activeFilters.visitType === 'virtual-care') {
      searchBody.visitType = 'virtual care offered';
    }

    if (activeFilters.afterHoursWeekend && Array.isArray(activeFilters.afterHoursWeekend) && activeFilters.afterHoursWeekend.length > 0) {
      searchBody.afterHoursWeekend = activeFilters.afterHoursWeekend;
    }

    if (activeFilters.groupAffiliation && Array.isArray(activeFilters.groupAffiliation) && activeFilters.groupAffiliation.length > 0) {
      searchBody.groupAffiliations = activeFilters.groupAffiliation;
    }

    if (activeFilters.agesServed) {
      const agesServedRaw = Array.isArray(activeFilters.agesServed)
        ? activeFilters.agesServed.filter(v => v !== 'no-preference')[0]
        : activeFilters.agesServed;
      if (agesServedRaw && agesServedRaw !== 'no-preference') {
        searchBody.agesServed = agesServedRaw;
      }
    }

    if (activeFilters.birthingFriendlyCare && activeFilters.birthingFriendlyCare !== 'no-preference') {
      searchBody.birthFriendly = 'birth-friendly care';
    }

    if (activeFilters.providerRace && Array.isArray(activeFilters.providerRace) && activeFilters.providerRace.length > 0) {
      searchBody.race = activeFilters.providerRace;
    }

    if (activeFilters.providerEthnicity && Array.isArray(activeFilters.providerEthnicity) && activeFilters.providerEthnicity.length > 0) {
      searchBody.ethnicity = activeFilters.providerEthnicity;
    }

    if (activeFilters.handicapAccess && Array.isArray(activeFilters.handicapAccess) && activeFilters.handicapAccess.length > 0) {
      searchBody.handicapAccessibility = activeFilters.handicapAccess;
    }

    if (activeFilters.specialProviderType && Array.isArray(activeFilters.specialProviderType) && activeFilters.specialProviderType.length > 0) {
      searchBody.specialProviderType = activeFilters.specialProviderType;
    }

    if (activeFilters.pubTrans && Array.isArray(activeFilters.pubTrans) && activeFilters.pubTrans.includes('yes')) {
      searchBody.pubTrans = 'yes';
    }

    if (activeFilters.hospitalAffiliation && Array.isArray(activeFilters.hospitalAffiliation) && activeFilters.hospitalAffiliation.length > 0) {
      searchBody.hospitalAffiliations = activeFilters.hospitalAffiliation;
    }

    if (activeFilters.specializedTraining && Array.isArray(activeFilters.specializedTraining) && activeFilters.specializedTraining.length > 0) {
      searchBody.specializedTraining = activeFilters.specializedTraining;
    }

    if (activeFilters.accreditation && Array.isArray(activeFilters.accreditation) && activeFilters.accreditation.length > 0) {
      searchBody.accreditation = activeFilters.accreditation;
    }

    if (activeFilters.cshcs && Array.isArray(activeFilters.cshcs) && activeFilters.cshcs.includes('yes')) {
      searchBody.cshcsProvider = 'yes';
    }

    if (activeFilters.specialty && Array.isArray(activeFilters.specialty) && activeFilters.specialty.length > 0) {
      searchBody.specialty = activeFilters.specialty;
    }

    if (subCategory && subCategory !== '') {
      searchBody.subCategory = subCategory;
      if (paramType === 'superCategory' && query) {
        searchBody.superCategory = query;
      }
    } else if (paramType === 'ps') {
      searchBody.psId = query;
    } else if (paramType === 'superCategory') {
      searchBody.superCategory = query;
    } else {
      searchBody.query = query;
    }

    return searchBody;
  };

  const fetchResults = async (query, page = 1, pageSize = resultsPerPage, paramType = 'query', overrideDistance = null, overridePlanCode = null, overrideFilters = null, subCategory = null) => {
    setLoading(true);
    setError('');

    try {
      // Check if running in local development
      const isLocalDevelopment = isLocalDevMode();
      // Fetch results from API or mock data
      let fetchedResults = [];
      let totalRecords = 0;
      let totalPages = 0;
      
      if (isLocalDevelopment) {        
        // Use hardcoded PA (environmentId "28") dummy data for local development —
        // both the provider search results AND the matching facets, so filters/UI
        // that depend on facets (e.g. sidebar filter option lists) also work without
        // a real backend connection. This branch previously computed fetchedResults/
        // totalRecords/totalPages but never actually called setResults/setTotalResults/
        // updateSearchState (those were only reachable from inside the `else` — real
        // API — branch below), so local dev search results silently never updated the
        // UI at all. Also previously used the generic `mockSearchResultsOrg` fixture
        // (a single unrelated Florida provider) rather than a plan actually hardcoded
        // to PA as requested.
        const searchTerm = (query || '').toLowerCase();
        fetchedResults = dummyPaSearchResponse.results.filter(provider =>
          provider.firstName?.toLowerCase().includes(searchTerm) ||
          provider.lastName?.toLowerCase().includes(searchTerm) ||
          provider.providerType?.toLowerCase().includes(searchTerm) ||
          provider.specialty?.[0]?.specialtyName?.toLowerCase().includes(searchTerm)
        );

        // If no specific matches, return all dummy results
        if (fetchedResults.length === 0) {
          fetchedResults = dummyPaSearchResponse.results;
        }

        totalRecords = dummyPaSearchResponse.totalRecords || fetchedResults.length;
        totalPages = dummyPaSearchResponse.totalPages || Math.ceil(totalRecords / pageSize);

        // Populate facets from the same dummy PA dataset so sidebar filters have
        // real-looking option lists instead of staying empty/erroring on localhost.
        setFacetsLoading(true);
        try {
          const parsed = parseFacets(dummyPaFacetsResponse);
          setFacets(parsed);
          setFacetsError('');
        } finally {
          setFacetsLoading(false);
        }

        setResults(fetchedResults);
        setTotalResults(totalRecords);
        updateSearchState({ currentPage: page });
      } else {
          // Use real API for non-local environments
          const searchConfig = config.SEARCH_API_CONFIG;

          if (!environmentId) {
            throw new Error('No environment ID available — please select a plan before searching.');
          }

          // Build the request body using the shared builder (also used by handleEmailResults
          // below) so the live search and the emailed results always stay in sync.
          const searchBody = buildProviderSearchBody(query, page, pageSize, paramType, overrideDistance, overridePlanCode, overrideFilters, subCategory);
          const allResultsUrl = `${config.APIC_BASE_URL_API}${searchConfig.providerSearchEndpoint}`;
          
          // Log the full API request every time fetchResults is called
          console.log('');
          console.log('═══════════════════════════════════════════════════════════');
          console.log('🔍 PROVIDER SEARCH API REQUEST');
          console.log('═══════════════════════════════════════════════════════════');
          console.log('Timestamp:', new Date().toISOString());
          console.log('Method: POST');
          console.log('Full URL:', allResultsUrl);
          console.log('POST Body:', JSON.stringify(searchBody, null, 2));
          console.log('Endpoint:', searchConfig.providerSearchEndpoint);
          console.log('Parameter Type:', paramType);
          console.log('═══════════════════════════════════════════════════════════');
          console.log('');
          
          // Make both API calls concurrently using Promise.all
          const [providerResponse, facetsData] = await Promise.all([
            // Provider search API call
            apicFetch(allResultsUrl, {
              method: 'POST',
              body: JSON.stringify(searchBody)
            }),
            
            // Facets API call - uses same search body as provider search
            (async () => {
              try {
                setFacetsLoading(true);
                const facetsResult = await fetchFacets(searchBody);
                const parsed = parseFacets(facetsResult);
                setFacets(parsed);
                setFacetsError('');
                return facetsResult;
              } catch (error) {
                console.error('⚠️ Facets API failed, continuing without facets:', error);
                setFacetsError(error.message);
                setFacets(null);
                return null; // Don't fail the entire search if facets fail
              } finally {
                setFacetsLoading(false);
              }
            })()
          ]);
          
          // The provider search endpoint responds with HTTP 404 (not 200 + an empty
          // array) when a search legitimately has zero matching providers — this is
          // normal, expected "no results" behavior, NOT a backend outage. Previously
          // `!providerResponse.ok` treated ANY non-2xx status as a hard failure and
          // threw, which surfaced the "Our provider search tool isn't working right
          // now" API error banner for what was really just a zero-result search —
          // stepping on the existing "No providers found" messaging that's meant to
          // handle that case instead. Only genuine failures (5xx, other 4xx, 429,
          // network/timeout errors) should still throw and show the error banner.
          if (providerResponse.status === 404) {
            console.log('ℹ️ Provider search returned 404 — treating as zero matching results (not an error).');
            fetchedResults = [];
            totalRecords = 0;
            totalPages = 0;
          } else {
          if (!providerResponse.ok) {
            console.log('');
            console.log('❌❌❌ SEARCH API ERROR ❌❌❌');
            console.error('❌ SEARCH API ERROR:', {
              timestamp: new Date().toISOString(),
              status: providerResponse.status,
              statusText: providerResponse.statusText,
              url: allResultsUrl
            });
            console.log('');
            throw new Error(`HTTP ${providerResponse.status}: ${providerResponse.statusText}`);
          }

          const data = await providerResponse.json();
          
          // Log the full API response
          console.log('');
          console.log('═══════════════════════════════════════════════════════════');
          console.log('✅ PROVIDER SEARCH API RESPONSE');
          console.log('═══════════════════════════════════════════════════════════');
          console.log('Timestamp:', new Date().toISOString());
          console.log('Status:', providerResponse.status, providerResponse.statusText);
          console.log('URL:', allResultsUrl);
          console.log('Total Records:', data.totalRecords);
          console.log('Total Pages:', data.totalPages);
          console.log('Current Page:', data.page);
          console.log('Results Count:', (data.content || data.results || data.response || []).length);
          console.log('Response Data:', data);
          console.log('Response Structure:', {
            hasContent: !!data.content,
            hasResults: !!data.results,
            hasResponse: !!data.response,
            hasTotalRecords: !!data.totalRecords,
            hasTotalPages: !!data.totalPages,
            topLevelKeys: Object.keys(data)
          });
          console.log('═══════════════════════════════════════════════════════════');
          console.log('');
          
          // Handle different response structures
          fetchedResults = data.content || data.results || data.response || [];
          totalRecords = data.totalRecords || fetchedResults.length;
          totalPages = data.totalPages || Math.ceil(totalRecords / pageSize);
          }

        // Set results directly from API/mock response
        
        setResults(fetchedResults)
        setTotalResults(totalRecords)
        updateSearchState({ currentPage: page });   }} 
           catch (err) {
      // Classify the failure (timeout / 5xx / gateway 4xx / 429 rate-limited / unknown)
      // and surface the shared, plan-aware APIC/MCS backend error message + Contact Us
      // link instead of a raw technical error string. See
      // reports/SPECIFIC_ERROR_MESSAGES_PLAN.md for the full story.
      const classification = classifyApiError({ error: err });
      console.error('❌ Provider search failed:', classification, err);
      setError(getErrorMessage(ERROR_TYPES.API));
      setResults([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  // Just update context — the watcher effect (Phase 2.5) fetches results for the new page.
  const handlePageChange = (newPage) => {
    if (newPage !== currentPage) {
      updateSearchState({ currentPage: newPage });
      scrollToResults();
    }
  };

  // Just update context — the watcher effect (Phase 2.5) fetches results with the new size.
  const handleResultsPerPageChange = (newResultsPerPage) => {
    updateSearchState({ resultsPerPage: newResultsPerPage, currentPage: 1 });
    scrollToResults();
  };

  // Scroll to top of results section
  const scrollToResults = () => {
    // Try to scroll to breadcrumb section first, otherwise scroll to top of page
    const breadcrumb = document.querySelector('.breadcrumb-section');
    const resultsContent = document.querySelector('.results-content');
    
    if (breadcrumb) {
      breadcrumb.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (resultsContent) {
      resultsContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle new search from search bar (update results without navigation to a different
  // page — the URL path stays exactly `/find-care/{env}/results` throughout).
  // Resets popularSearchId/superCategory/subCategory/psLabel so a plain-text search always
  // takes precedence — mirrors the old handler's URL cleanup (newSearchParams.delete('ps'/
  // 'superCategory'/'subCategory')), just against context instead of the query string.
  // The watcher effect (Phase 2.5) fetches results; no direct fetchResults call needed here.
  //
  // Phase 5 (Browser Navigation Support): also calls `navigate()` to the SAME path (no query
  // string — the URL never changes) purely to create a new browser history entry, carrying
  // the complete resulting state as that entry's `state` payload. This is what lets the
  // browser's own back/forward buttons step between distinct searches performed on this page
  // without ever leaving it — see the Phase 5 effect above (keyed on `routerLocation.key`)
  // for how that state gets read back and reapplied. `updateSearchState` below is still
  // called directly too (not left solely to that effect) so the search feels instant — the
  // effect's redundant re-application on this same action is a harmless, same-value no-op,
  // already deduped by the Phase 2.5 watcher's own searchKey guard against a real duplicate
  // fetch.
  const handleNewSearch = (newQuery, isSuperCategory = false, subCategoryVal = '') => {
    if (newQuery && newQuery.trim()) {
      const trimmedQuery = newQuery.trim();
      // When the SearchBar's typeahead suggestion came from the typesOfCare/whereToGetCare/
      // healthConditions categories, run it as a super-category search instead of a plain
      // free-text query search — see search.jsx's handleSearch for the matching logic used
      // on the initial search page. `subCategoryVal` is the specific sub-category the user
      // picked (SearchBar passes suggestion.externalLinkValue as the 3rd arg) — previously
      // this handler ignored it and always sent subCategory: '', so the same typeahead pick
      // produced a broader superCategory-only search here than it did on the search page.
      const newState = {
        query: isSuperCategory ? '' : trimmedQuery,
        superCategory: isSuperCategory ? trimmedQuery : '',
        subCategory: subCategoryVal || '',
        popularSearchId: '',
        psLabel: '',
        // A brand-new search starts from default filters (same as search.jsx's new
        // searches), keeping only the current distance. Sort is part of filters, so it
        // resets to default too.
        filters: { ...DEFAULT_FILTERS, distance: filters.distance || DEFAULT_FILTERS.distance },
        currentPage: 1
        // location and selectedPlan are left as-is
      };
      updateSearchState(newState);
      // Remount the "All filters" overlay so its local state (pending selections, custom
      // distance, expanded sections, open/closed) resets along with the filters.
      setFiltersResetKey(k => k + 1);
      setExpandedStates({});

      navigate(`/find-care/${resolvedEnvPlan}/results`, {
        state: { searchStateSnapshot: { ...searchState, ...newState } }
      });
    }
  };

  // Print functionality - formats and prints results.
  // scope: 'firstPage' (default) prints what's currently loaded on screen;
  //        'all' fetches every matching provider first, then prints them all.
  const SCOPE_ALL = 'all';

  // Fetches every matching provider (not just the current page) using the same request
  // body as the live search, paging in chunks of 100 (the API's max page size, see
  // buildProviderSearchBody's Math.min(pageSize, 100)).
  const fetchAllResultsForExport = async () => {
    const PAGE_SIZE = 100;
    const total = totalResults || results.length;
    const totalPagesNeeded = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const searchType = determineSearchType();
    const url = `${config.APIC_BASE_URL_API}${config.SEARCH_API_CONFIG.providerSearchEndpoint}`;

    const pages = await Promise.all(
      Array.from({ length: totalPagesNeeded }, async (_, i) => {
        const body = buildProviderSearchBody(
          searchQuery, i + 1, PAGE_SIZE, searchType, filters.distance, selectedPlan, filters, subCategory
        );
        const response = await apicFetch(url, { method: 'POST', body: JSON.stringify(body) });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        return data.content || data.results || data.response || [];
      })
    );
    return pages.flat();
  };

  const handlePrintResults = async (scope = 'firstPage') => {
    if (results.length === 0) {
      alert('No results to print');
      return;
    }

    handleEmailResults("print", scope);
    // Open the window synchronously (inside the user's click) so popup blockers allow it,
    // even when we have to wait on a network fetch for the "all results" scope.
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      alert('Please allow pop-ups for this site to print your results.');
      return;
    }

    let listToPrint = results;
    if (scope === SCOPE_ALL) {
      printWindow.document.open();
      printWindow.document.write('<p style="font-family: Arial, sans-serif; margin: 20px;">Preparing all results...</p>');
      printWindow.document.close();
      try {
        const all = await fetchAllResultsForExport();
        if (all.length > 0) listToPrint = all;
      } catch (error) {
        console.error('❌ Failed to fetch all results for printing — falling back to current page:', error);
      }
    }

    // Create printable content
    const printContent = generatePrintableHTML(listToPrint, scope);
    
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        // Don't automatically close - let user close after printing
        // printWindow.close();
      }, 500); // Give a bit more time for content to render
    };
  };

  // Generate HTML content for printing
  const generatePrintableHTML = (providers = results, scope = 'firstPage') => {
    const planDisplayName = getPlanDisplayName();
    const printDate = new Date().toLocaleDateString();
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Provider Search Results</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.4;
            }
            .header { 
              border-bottom: 2px solid #333; 
              padding-bottom: 10px; 
              margin-bottom: 20px; 
            }
            .search-info { 
              margin-bottom: 20px; 
              background: #f5f5f5; 
              padding: 10px; 
            }
            .provider-card { 
              border: 1px solid #ddd; 
              margin-bottom: 15px; 
              padding: 15px; 
              page-break-inside: avoid;
            }
            .provider-name { 
              font-size: 18px; 
              font-weight: bold; 
              margin-bottom: 8px; 
            }
            .provider-detail { 
              margin-bottom: 5px; 
            }
            .provider-detail strong { 
              display: inline-block; 
              min-width: 120px; 
            }
            .footer { 
              margin-top: 30px; 
              font-size: 12px; 
              color: #666; 
              border-top: 1px solid #ddd; 
              padding-top: 10px; 
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Provider Search Results</h1>
          </div>
          
          <div class="search-info">
            <div><strong>Search Query:</strong> ${searchDisplayQuery || searchQuery}</div>
            ${planDisplayName ? `<div><strong>Plan:</strong> ${planDisplayName}</div>` : ''}
            <div><strong>Results Page:</strong> ${scope === 'all' ? 'All' : currentPage}</div>
            <div><strong>Results Shown:</strong> ${providers.length} of ${totalResults}</div>
            <div><strong>Print Date:</strong> ${printDate}</div>
          </div>

          <div class="results-list">
            ${providers.map(provider => {
              // Match the UI's source of truth exactly (providerSummary.jsx reads
              // provider.locations[0] for address/phone/county, NOT top-level
              // provider.provPhyAddr1/phone/provPhyCounty) — the top-level fields can be
              // stale, missing, or simply reflect a different location than what's shown
              // on screen, which was why the printed/emailed address didn't match the UI.
              const primaryLocation = (provider.locations && provider.locations[0]) || {};
              const addr1 = primaryLocation.provPhyAddr1 || provider.provPhyAddr1;
              const addr2 = primaryLocation.provPhyAddr2 || provider.provPhyAddr2;
              const city = primaryLocation.provPhyCity || provider.provPhyCity;
              const state = primaryLocation.provPhyState || provider.provPhyState;
              const zip = primaryLocation.provPhyZip || provider.provPhyZip;
              const phone = primaryLocation.phone || provider.phone;
              const county = primaryLocation.provPhyCounty || provider.provPhyCounty;
              return `
              <div class="provider-card">
                <div class="provider-name">
                  ${provider.firstName ? `${provider.firstName} ${provider.lastName}`.trim() : provider.lastName || 'Provider name not available'}
                </div>
                
                ${provider.specialty && provider.specialty.length > 0 ? `
                  <div class="provider-detail">
                    <strong>Specialty:</strong> ${provider.specialty[0].specialtyName || 'Not specified'}
                  </div>
                ` : ''}
                
                ${provider.providerType ? `
                  <div class="provider-detail">
                    <strong>Provider Type:</strong> ${provider.providerType}
                  </div>
                ` : ''}
                
                <div class="provider-detail">
                  <strong>Address:</strong> 
                  ${[addr1, addr2, `${city}, ${state} ${zip}`].filter(Boolean).join('<br>')}
                  ${provider.locations && provider.locations.length > 1 ? '<br><em>Multiple locations</em>' : ''}
                </div>
                
                ${phone ? `
                  <div class="provider-detail" translate='no'>
                    <strong>Phone:</strong> ${phone}
                  </div>
                ` : ''}
                
                ${county ? `
                  <div class="provider-detail">
                    <strong>County:</strong> ${county}
                  </div>
                ` : ''}
                
                ${provider.language ? `
                  <div class="provider-detail">
                    <strong>Languages:</strong> ${provider.language}
                  </div>
                ` : ''}
                
                ${provider.specialty && provider.specialty[0] && provider.specialty[0].boardCertifiedFlag === 'Yes' ? `
                  <div class="provider-detail">
                    <strong>Board Certified:</strong> Yes
                  </div>
                ` : ''}
                
                ${provider.newAssigns === '1' ? `
                  <div class="provider-detail">
                    <strong>Accepting New Patients:</strong> Yes
                  </div>
                ` : provider.newAssigns === '0' ? `
                  <div class="provider-detail">
                    <strong>Accepting New Patients:</strong> No
                  </div>
                ` : ''}
                
                ${provider.handicapAccess === 'Y' ? `
                  <div class="provider-detail">
                    <strong>Wheelchair Accessible:</strong> Yes
                  </div>
                ` : provider.handicapAccess === 'N' ? `
                  <div class="provider-detail">
                    <strong>Wheelchair Accessible:</strong> No
                  </div>
                ` : ''}
                
                ${provider.ageMin || provider.ageMax ? `
                  <div class="provider-detail">
                    <strong>Age Range:</strong> ${provider.ageMin || '0'} - ${provider.ageMax || '99'} years
                  </div>
                ` : ''}
                
                ${provider.provSex ? `
                  <div class="provider-detail">
                    <strong>Gender:</strong> ${provider.provSex === 'M' ? 'Male' : provider.provSex === 'F' ? 'Female' : provider.provSex}
                  </div>
                ` : ''}
                
                ${provider.specialty && provider.specialty.length > 1 ? `
                  <div class="provider-detail">
                    <strong>Additional Specialties:</strong> ${provider.specialty.slice(1).map(s => s.specialtyName).filter(Boolean).join(', ')}
                  </div>
                ` : ''}
                
                ${provider.npiId ? `
                  <div class="provider-detail">
                    <strong>NPI ID:</strong> ${provider.npiId}
                  </div>
                ` : ''}
                
                ${provider.extendedOfficeHours ? `
                  <div class="provider-detail">
                    <strong>Extended Office Hours:</strong> ${provider.extendedOfficeHours === 'Y' ? 'Yes' : 'No'}
                  </div>
                ` : ''}
                
                ${provider.weekendHours ? `
                  <div class="provider-detail">
                    <strong>Weekend Hours:</strong> ${provider.weekendHours === 'Y' ? 'Yes' : 'No'}
                  </div>
                ` : ''}
                
                ${provider.fromMonday && provider.toMonday ? `
                  <div class="provider-detail">
                    <strong>Office Hours:</strong><br>
                    Monday: ${provider.fromMonday} - ${provider.toMonday}<br>
                    ${provider.fromTuesday && provider.toTuesday ? `Tuesday: ${provider.fromTuesday} - ${provider.toTuesday}<br>` : ''}
                    ${provider.fromWednesday && provider.toWednesday ? `Wednesday: ${provider.fromWednesday} - ${provider.toWednesday}<br>` : ''}
                    ${provider.fromThursday && provider.toThursday ? `Thursday: ${provider.fromThursday} - ${provider.toThursday}<br>` : ''}
                    ${provider.fromFriday && provider.toFriday ? `Friday: ${provider.fromFriday} - ${provider.toFriday}<br>` : ''}
                    ${provider.fromSaturday && provider.toSaturday ? `Saturday: ${provider.fromSaturday} - ${provider.toSaturday}<br>` : ''}
                    ${provider.fromSunday && provider.toSunday ? `Sunday: ${provider.fromSunday} - ${provider.toSunday}` : ''}
                  </div>
                ` : ''}
                
                ${provider.telemedicine ? `
                  <div class="provider-detail">
                    <strong>Telemedicine:</strong> ${provider.telemedicine === 'Y' ? 'Available' : provider.telemedicine}
                  </div>
                ` : ''}
                
                ${provider.telehealth ? `
                  <div class="provider-detail">
                    <strong>Telehealth:</strong> ${provider.telehealth === 'Y' ? 'Available' : provider.telehealth}
                  </div>
                ` : ''}
                
                ${provider.telehealthDaysHours ? `
                  <div class="provider-detail">
                    <strong>Telehealth Hours:</strong> ${provider.telehealthDaysHours}
                  </div>
                ` : ''}
                
                ${provider.affiliations && provider.affiliations.length > 0 ? `
                  <div class="provider-detail">
                    <strong>Service Affiliations:</strong><br>
                    ${provider.affiliations.map(affil => 
                      `${affil.affiliateName}${affil.affilType ? ` (${affil.affilType})` : ''}`
                    ).join('<br>')}
                  </div>
                ` : ''}
                
                <div class="provider-detail">
                  <strong>Group Affiliations:</strong><br>
                  ${(() => {
                    // Same rule as the UI card (providerDetailsType3/4.jsx): GROUP or DIRECT.
                    const groupAffiliationNames = (provider.affiliations || [])
                      .filter(aff => aff.affilType === 'GROUP' || aff.affilType === 'DIRECT')
                      .map(aff => aff.affiliateName)
                      .filter(Boolean);
                    return groupAffiliationNames.length > 0
                      ? groupAffiliationNames.join('<br>')
                      : 'None found';
                  })()}
                </div>

                <div class="provider-detail">
                  <strong>Hospital Affiliations:</strong><br>
                  ${(() => {
                    // Match the UI card's logic exactly (providerDetailsType3/4.jsx): only
                    // provider.affiliations entries explicitly typed 'HOSP STAFF' count as
                    // hospital affiliations — NOT the separate provider.hospitals array
                    // (which is accreditation-source data, a different concept, despite the
                    // similar name). Previously this read provider.hospitals directly, which
                    // could show many more/different entries here than the UI card shows
                    // (which may correctly report "None found" for the same provider).
                    const hospitalAffiliationNames = provider.affiliations && provider.affiliations.length > 0
                      ? provider.affiliations
                          .filter(aff => aff.affilType === 'HOSP STAFF')
                          .map(aff => aff.affiliateName)
                          .filter(Boolean)
                      : [];
                    return hospitalAffiliationNames.length > 0
                      ? hospitalAffiliationNames.join('<br>')
                      : 'None found';
                  })()}
                </div>
              </div>
            `;
            }).join('')}
          </div>

          <div class="footer">
            <p>This directory was generated on ${printDate}. Provider information is subject to change. Please contact providers directly to confirm availability and services.</p>
            <p>For the most up-to-date provider information, please visit our website or contact customer service.</p>
          </div>
        </body>
      </html>
    `;
  };

  // Email functionality - sends formatted results to user's email.
  // scope: 'firstPage' (default) emails the current page; 'all' asks the backend to
  // re-run the search with page 0 and a size covering every result.
  const handleEmailResults = async (emailAddress, scope = 'firstPage') => {
    if (results.length === 0) {
      setEmailError('No results to email');
      return;
    }

    setEmailLoading(true);
    setEmailError('');
    let programID = results[0]?.programId || ''; // Fallback program ID if not available in results
    try {
      // Build the exact same providerSearchRequest body used for the live on-screen
      // search (lat/long, planCode, sortBy, and every active filter), so the server-side
      // re-search the digital-deliveries endpoint performs returns the same providers
      // that are currently shown on this results page — see buildProviderSearchBody for
      // the full explanation of why the old minimal body caused missing providers.
      const searchType = determineSearchType();
      const providerSearchRequest = buildProviderSearchBody(
        searchQuery,
        currentPage,
        resultsPerPage,
        searchType,
        filters.distance,
        selectedPlan,
        filters,
        subCategory
      );
      // No `zip` — providerSearchRequest is exactly the same payload the on-screen
      // search sends to the provider search endpoint.

      let allResults;
      if (scope === 'all') {
        // Ask the backend to cover every result, not just the current page.
        allResults = true;
      } else {
        allResults = false;
      }

      // Prepare email data
      const emailData = {
        email: emailAddress === 'print' ? '' : emailAddress, // Don't send an email if printing
        programId: programID,
        allResults,
        printOrEmail: emailAddress === 'print' ? 'print' : 'email',
        providerSearchRequest
        // Commenting this below code to sync with BE request prama..
        // searchQuery: searchDisplayQuery || searchQuery,
        // planName: getPlanDisplayName(),
        // currentPage: currentPage,
        // resultsCount: results.length,
        // totalResults: totalResults,
        // printDate: new Date().toLocaleDateString(),
        // results: results.map(provider => ({
        //   // Map provider data to clean structure for email
        //   name: provider.firstName ? `${provider.firstName} ${provider.lastName}`.trim() : provider.lastName || 'Provider name not available',
        //   specialty: provider.specialty && provider.specialty.length > 0 ? provider.specialty[0].specialtyName : null,
        //   providerType: provider.providerType || null,
        //   address: [
        //     provider.provPhyAddr1,
        //     provider.provPhyAddr2,
        //     `${provider.provPhyCity}, ${provider.provPhyState} ${provider.provPhyZip}`
        //   ].filter(Boolean).join(', '),
        //   phone: provider.phone || null,
        //   county: provider.provPhyCounty || null,
        //   languages: provider.language || null,
        //   boardCertified: provider.specialty && provider.specialty[0] && provider.specialty[0].boardCertifiedFlag === 'Yes'
        // }))
      };

      // Construct API URL for email endpoint
      const searchConfig = config.SEARCH_API_CONFIG;
      const { results: emailResults, ...scalarEmailData } = emailData;
      const params = new URLSearchParams(scalarEmailData);
      params.set('results', JSON.stringify(emailResults));
      let apiUrl;

      if (import.meta.env.DEV && window.location.hostname === 'localhost') {
        apiUrl = `/api-proxy/hcb/v3/providersearchresults/providers/digitaldeliveries`;
      } else {
        apiUrl = `${config.APIC_BASE_URL_API}/hcb/v3/providersearchresults/providers/digitaldeliveries`;
      }

      const response = await apicFetch(apiUrl, {
        method: 'POST',
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = (response.headers.get('content-type') || '').toLowerCase();

      if (emailAddress === 'print' || contentType.includes('application/pdf')) {
        // Print delivery: the endpoint returns the PDF itself (application/pdf), not JSON,
        // so calling response.json() would throw. Read it as a blob and download it
        // directly via a temporary <a download> link — no iframe, popup or new tab, so
        // popup blockers can't interfere even though we're past an await here.
        if (!contentType.includes('application/pdf')) {
          throw new Error(`Expected a PDF for print delivery but got "${contentType || 'unknown'}"`);
        }
        const pdfBlob = await response.blob();
        const pdfUrl = URL.createObjectURL(pdfBlob);

        // Prefer the filename the server suggests (Content-Disposition), else a default.
        const disposition = response.headers.get('content-disposition') || '';
        const filenameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
        let fileName = 'provider-search-results.pdf';
        if (filenameMatch) {
          try {
            fileName = decodeURIComponent(filenameMatch[1]);
          } catch {
            fileName = filenameMatch[1];
          }
        }
        if (!/\.pdf$/i.test(fileName)) fileName += '.pdf';

        const downloadLink = document.createElement('a');
        downloadLink.href = pdfUrl;
        downloadLink.download = fileName;
        downloadLink.style.display = 'none';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // Release the blob URL once the browser has had time to start the download.
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
      } else {
        const responseData = await response.json();
      }

      setEmailSuccess(true);

    } catch (error) {
      // Surface the shared, plan-aware SMTP email error message instead of a raw
      // technical string. See reports/SPECIFIC_ERROR_MESSAGES_PLAN.md.
      console.error('❌ Failed to send results email:', classifyApiError({ error }), error);
      setEmailError(getErrorMessage(ERROR_TYPES.EMAIL));
    } finally {
      setEmailLoading(false);
    }
  };

  // Handle opening email modal
  const handleOpenEmailModal = () => {
    setShowEmailModal(true);
    setEmailError('');
    setEmailSuccess(false);
  };

  // Handle closing email modal
  const handleCloseEmailModal = () => {
    if (!emailLoading) {
      setShowEmailModal(false);
      setEmailError('');
      setEmailSuccess(false);
    }
  };

  // Handle opening incorrect info modal
  const handleOpenIncorrectInfoModal = (provider, location = null) => {
    // If location is provided, merge location-specific data with provider data
    const providerData = location ? {
      ...provider,
      // Override with location-specific data
      provPhyAddr1: location.provPhyAddr1,
      provPhyAddr2: location.provPhyAddr2,
      provPhyCity: location.provPhyCity,
      provPhyState: location.provPhyState,
      provPhyZip: location.provPhyZip,
      phone: location.phone,
      fax: location.fax,
      url: location.url,
      distanceInMiles: location.distanceInMiles,
      // Keep the location reference for additional context
      selectedLocation: location
    } : provider;
    
    setSelectedProviderForFeedback(providerData);
    setShowIncorrectInfoModal(true);
  };

  // Handle closing incorrect info modal
  const handleCloseIncorrectInfoModal = () => {
    setShowIncorrectInfoModal(false);
    setSelectedProviderForFeedback(null);
  };

  // Handle opening distance panel
  const handleOpenDistancePanel = () => {
    setIsDistancePanelOpen(true);
  };

  // Handle closing distance panel
  const handleCloseDistancePanel = () => {
    setIsDistancePanelOpen(false);
  };

  // Handle distance selection from distance panel
  const handleDistanceSelect = (newDistance) => {    
    // Update the distance filter
    handleFilterChange('distance', newDistance);
  };

  // Sort is now just another filter field (filters.sort) living in context — no more
  // direct window.history.replaceState manipulation. Previously this bypassed React
  // Router's setSearchParams entirely, which meant the OLD main effect's
  // `searchParams.get('sort')` re-read (fired on every subsequent filter change) never
  // actually saw the sort value that was "written" this way, silently reverting the user's
  // sort choice back to 'Distance' the next time any other filter changed. Routing sort
  // through the same updateFilters() as everything else fixes that class of bug for good.
  const handleSortChange = (newSortOption) => {
    updateFilters({ sort: newSortOption });
  };

  // Sorting is now performed server-side via the `sortBy` param sent in fetchResults'
  // searchBody (name_asc/name_desc/omitted-for-distance) — see handleSortChange and the
  // searchBody assembly above. `results` therefore already arrives in the correct order
  // for the current page, so this is now just a passthrough (kept as its own binding,
  // rather than renaming every `sortedResults` usage below, to minimize the diff).
  // Previously this re-sorted `results` client-side, which only ever re-ordered the
  // CURRENT PAGE's already-fetched providers rather than the full result set across all
  // pages — e.g. Name (A–Z) only looked alphabetized within page 1, and page 2 restarted
  // from whatever order the API/distance-search happened to return, not continuing the
  // alphabetical sequence. Letting the API sort before pagination fixes that for good.
  const sortedResults = results;

  const renderProvider = (provider, index) => {
    const fieldConfig = getSearchResultFields();
    
    // Handle both individual providers and healthcare facilities
    const displayName = provider.firstName 
      ? `${provider.firstName} ${provider.lastName}`.trim()
      : provider.lastName || 'Provider name not available';
      const isExpanded = expandedStates[`providerDetails-${provider.provId}`] || false;
      const isLastItem = index === results.length - 1;
      const toggleExpanded = (optionId) => {
        setExpandedStates(prev => ({
            ...prev,
            [optionId]: !prev[optionId]
        }));
    };
    const providerType = provider.providerType || '';
    const providerUrl = provider.providerUrl || '';
    const providerHandicap = provider.providerHandicap || '';
    const providerVirtualCare = provider.providerVirtualCare || '';
    const providerGender = provider.providerGender || '';
    const providerSpecialties = provider.providerSpecialties || '';
    const providerGroupAffiliation = provider.providerGroupAffiliation || '';
    const providerHospitalAffiliations = provider.providerHospitalAffiliations || '';
    const providerSpecialTraining = provider.providerSpecialTraining || '';
    const providerNpiId = provider.npiId || '';
    const providerStateLicense = provider.stateLicenseNumber || '';
    const specialty = provider.specialty && provider.specialty.length > 0 
      ? provider.specialty[0].specialtyName 
      : provider.providerType || 'Specialty not listed';
    const boardCertified = Array.isArray(provider.specialty) &&
      provider.specialty.some(spec => spec?.boardCertifiedFlag === 'Yes' || spec?.boardCertifiedFlag === 'Y');
      
    
    const address = [
      provider.provPhyAddr1,
      provider.provPhyAddr2,
      `${provider.provPhyCity}, ${provider.provPhyState} ${provider.provPhyZip}`
    ].filter(Boolean).join('\n');

    const sanitizedPhone = provider.phone ? sanitizePhoneForHref(provider.phone) : null;

    const providerNameId = `provider-name-${provider.provId}-${index}`;
    const providerDetailsType = getProviderDetailsType(provider.providerType, provider.specialty);

    return (
      // A11y fix: this <li> previously also had role="region" + aria-labelledby, which
      // overrides its native listitem semantics with a "region" landmark instead — QA
      // (8/5) confirmed the list-item + heading (providerNameId points at the <h3> name
      // in ProviderSummary) is already sufficient to distinguish each card, and the
      // extra region role/label was actively unhelpful (AT users lost the "list, N
      // items" navigation entirely, since role="region" is not list-item semantics).
      // providerNameId is left in place (still passed down and still used as the <h3>'s
      // id) since removing that would strip the accessible name from the heading itself.
      <li key={`${provider.provId}-${index}`} className="provider-card">
        <ProviderSummary
          provider={provider}
          fieldConfig={fieldConfig}
          address={address}
          sanitizedPhone={sanitizedPhone}
          specialty={specialty}
          boardCertified={boardCertified}
          providerNameId={providerNameId}
          providerDetailsType={providerDetailsType}
        />

        <div className="provider-details">
          <div key={`providerDetails-${provider.provId}-${index}`}>
                            <h4 className="provider-section-heading">
                              <a
                                href="#"
                                className={`providerLink ${isExpanded ? 'expanded' : ''}`}
                                aria-expanded={isExpanded}
                                aria-controls={`providerDetails-content-${provider.provId}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  toggleExpanded(`providerDetails-${provider.provId}`);
                                }}
                              >
                                <span>Provider details</span>
                                <img 
                                    src={downArrow} 
                                    className={`arrowDown ${isExpanded ? 'rotated' : ''}`} 
                                    alt="" 
                                    aria-hidden="true" 
                                />
                              </a>
                            </h4>
                            
                            <div 
                                id={`providerDetails-content-${provider.provId}`}
                                className={`expandable-content detail ${expandedStates[`providerDetails-${provider.provId}`] ? 'expanded' : ''}`}
                                aria-hidden={!expandedStates[`providerDetails-${provider.provId}`]}
                            >
                                {(() => {
                                  const detailsType = getProviderDetailsType(provider.providerType, provider.specialty);
                                  if (detailsType === 2) return (
                                    <ProviderDetailsType2
                                      provider={provider}
                                      providerSpecialty={provider.specialty}
                                      isExpanded={isExpanded}
                                    />
                                  );
                                  if (detailsType === 3) return (
                                    <ProviderDetailsType3
                                      provider={provider}
                                      providerSpecialty={provider.specialty}
                                      isExpanded={isExpanded}
                                    />
                                  );
                                  if (detailsType === 4) return (
                                    <ProviderDetailsType4
                                      provider={provider}
                                      providerSpecialty={provider.specialty}
                                      isExpanded={isExpanded}
                                    />
                                  );
                                  return (
                                    <ProviderDetails
                                      provider={provider}
                                      providerSpecialty={provider.specialty}
                                      isExpanded={isExpanded}
                                    />
                                  );
                                })()}
                            </div>
                        </div>
          </div>

          <div className="provider-service-details">
            <div key={`serviceLocation-${provider.provId}-${index}`}>
                            <h4 className="provider-section-heading">
                              <a
                                href="#"
                                className={`serviceLink ${expandedStates[`serviceLocation-${provider.provId}`] ? 'expanded' : ''}`}
                                aria-expanded={expandedStates[`serviceLocation-${provider.provId}`] || false}
                                aria-controls={`serviceLocation-content-${provider.provId}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  toggleExpanded(`serviceLocation-${provider.provId}`);
                                }}
                              >
                                <span>Location details</span>
                                <img 
                                    src={downArrow} 
                                    className={`arrowDown ${expandedStates[`serviceLocation-${provider.provId}`] ? 'rotated' : ''}`} 
                                    alt="" 
                                    aria-hidden="true" 
                                />
                              </a>
                            </h4>
                            
                            <div 
                                id={`serviceLocation-content-${provider.provId}`}
                                className={`expandable-content service ${expandedStates[`serviceLocation-${provider.provId}`] ? 'expanded' : ''}`}
                                aria-hidden={!expandedStates[`serviceLocation-${provider.provId}`]}
                            >
                                <ProviderServiceDetails
                                  serviceLocations={provider.locations}
                                  providerLanguages={provider.language}
                                  provider={provider}
                                  providerDetailsType={providerDetailsType}
                                  onIncorrectInfoClick={handleOpenIncorrectInfoModal}
                                  isExpanded={expandedStates[`serviceLocation-${provider.provId}`] || false}
                                />
                            </div>
                        </div>
          </div>
        </li>
    );
  };

  return (
    <main className="container-fluid results-page">
      {/* Breadcrumb Section */}
      <div className="breadcrumb-section">
        <Link to={`/find-care/${resolvedEnvPlan || 'FL'}`} className="breadcrumb-link">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
            <path d="M0 8.75C0 8.96313 0.127228 9.24612 0.256319 9.38345L7.52897 17.2125C7.86852 17.5682 8.41861 17.6094 8.81448 17.2485C9.16837 16.9258 9.17719 16.2964 8.84993 15.946L3.01193 9.67123L19.0909 9.67123C19.593 9.67123 20 9.25888 20 8.75018C20 8.24149 19.593 7.82909 19.0909 7.82909L3.01193 7.82909L8.84993 1.55435C9.17714 1.20398 9.15391 0.590103 8.81448 0.25194C8.45607 -0.105059 7.86333 -0.0732822 7.52897 0.287861L0.256319 8.11687C0.0426369 8.32922 0.00359154 8.53374 0 8.75Z" fill="#8932AF"/>
          </svg>
          Back to Find care
        </Link>
      </div>

      {/* Search Results Header Section */}
      <div className="search-results-header-section">
        <h1 className="search-results-title">Search results</h1>
        
      </div>

      {/* Search Location Info Section */}
      <div className="search-location-info-section">
        {selectedPlan && (planFeatures?.planCode || []).length > 1 && (
          <>
            <span className="plan-label">Your Plan:</span>
            <span className="plan-text">{getPlanDisplayName()}</span>
          </>
        )}
        <span className="search-near-label">Search near:</span>
        <span className="location-text">{locationDisplay}</span>
      </div>

      {/* Search Bar Section */}
      <div className="search-bar-section">
        <SearchBar
          onSearch={handleNewSearch}
          placeholder="Search for providers, places of care, or types of care..."
          initialValue={searchDisplayQuery || searchQuery}
          selectedPlan={selectedPlan}
          radiusMiles={filters.distance}
        />
      </div>

      {/* Provider Filters Section */}
      <ProviderFilters 
        key={filtersResetKey}
        filters={filters}
        onFilterChange={handleFilterChange}
        onBatchFilterChange={handleBatchFilterChange}
        facets={facets}
      />

      {/* Results Summary Section */}
      <ResultsSummary
        totalResults={totalResults}
        searchQuery={searchDisplayQuery || searchQuery}
        currentPage={currentPage}
        resultsPerPage={resultsPerPage}
        distance={filters.distance || "25"}
        location={locationDisplay}
        providers={results}
        onEditDistance={handleOpenDistancePanel}
      />

      {/* Results Control Bar */}
      <ResultsControlBar 
        sortOption={sortOption}
        onSortChange={handleSortChange}
        onPrintEmailClick={handleOpenEmailModal}
      />

      {/* Results Content */}
      <div className="results-content">
        {/* Feature Flags Loading State */}
        {planFeaturesLoading && !planFeatures && (
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>Loading plan configuration...</p>
          </div>
        )}

        {/* Search Results Loading State */}
        {!planFeaturesLoading && loading && (
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>Searching for providers...</p>
          </div>
        )}

        {/* Provider Search API Error State intentionally NOT rendered here — per
            request, the results page should only ever show the "No providers found"
            messaging below, regardless of how the search response comes back (error,
            zero results, etc.). `error` state is still tracked/logged elsewhere for
            diagnostics, it's just not surfaced as a banner on this page. */}

        {!planFeaturesLoading && !loading && results.length === 0 && searchQuery && (
          <div className="no-results">
            <h2>No providers found</h2>
            <p>
              Try expanding the distance for your search. Or try another search term. 
              Can't find a certain provider? They may not be in our network. 
              Need help?{' '}
              {/* Was `|| 'https://www.aetnabetterhealth.com/florida/contact-us.html'` — a
                  hardcoded, state-specific fallback that silently sent every OTHER
                  state's plan (PA, etc.) to Florida's contact page whenever
                  getFooterLinks().contactUs wasn't yet available, instead of just
                  failing safely. Render plain, non-link text in that case — never
                  misdirect the user to a guessed, likely-wrong state's page. */}
              {getFooterLinks()?.contactUs ? (
                <a
                  href={getFooterLinks().contactUs}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact us
                </a>
              ) : (
                'Contact us'
              )}.
            </p>
          </div>
        )}

        {!planFeaturesLoading && !loading && (
          <>
            {sortedResults.length > 0 && (
              <>
                <h2 className="sr-only">Provider info</h2>
                <ul className="results-list">
                  {sortedResults.map((provider, index) => renderProvider(provider, index))}
                </ul>
              </>
            )}

            <Pagination 
              currentPage={currentPage}
              totalPages={sortedResults.length > 0 ? Math.ceil(totalResults / resultsPerPage) || 10 : 1}
              totalResults={totalResults}
              resultsPerPage={resultsPerPage}
              onPageChange={handlePageChange}
              onResultsPerPageChange={handleResultsPerPageChange}
              hasResults={sortedResults.length > 0}
            />
          </>
        )}

        {/* Email / Print Modal */}
        {showEmailModal && (
          <PrintEmailModal
            isOpen={showEmailModal}
            onClose={handleCloseEmailModal}
            onSendEmail={handleEmailResults}
            onPrint={handlePrintResults}
            loading={emailLoading}
            error={emailError}
            success={emailSuccess}
            contactUsUrl={getErrorContactUsUrl()}
            totalResults={totalResults}
          />
        )}

        {/* Incorrect Info Modal — portaled to document.body. .results-page (an
            ancestor of this render location) has `transform: translateZ(0)` (see
            results/styles.css) to work around an Edge/Chromium compositor bug. That
            transform creates a new containing block for `position: fixed`
            descendants, so without a portal this modal's fixed overlay would be
            positioned relative to .results-page instead of the viewport — rendering
            it off-position/invisible, making the "Tell us about incorrect info" link
            appear to do nothing. Matches the same portal pattern already used for
            this scenario in providerSummary.jsx/providerServiceDetails.jsx/
            providerDetailsType2.jsx/hoverTooltip.jsx. */}
        {showIncorrectInfoModal && selectedProviderForFeedback && createPortal(
          <IncorrectInfoModal
            isOpen={showIncorrectInfoModal}
            onClose={handleCloseIncorrectInfoModal}
            provider={selectedProviderForFeedback}
          />,
          document.body
        )}

        {/* Distance Panel */}
        <DistancePanel
          isOpen={isDistancePanelOpen}
          onClose={handleCloseDistancePanel}
          currentDistance={filters.distance || '25'}
          onDistanceSelect={handleDistanceSelect}
        />
      </div>
    </main>
  );
};

export default Results;
